/**
 * ComprasView -- #/compras: gestion de compras + movimientos inventario (desperdicios/danos/devoluciones/aumentos).
 *
 * 4 tabs:
 *   1. Facturas      -- listado facturas proveedor con badge match_status (PENDING/MATCH/REVIEW)
 *   2. Detalle       -- items de la factura seleccionada + candidatos de matcheo al inventario
 *   3. Movimiento    -- form para registrar desperdicio/dano/devolucion/aumento manual
 *   4. Aprobaciones  -- ActionArea: subir factura OCR -> render tabla items_pendientes interactiva
 *                       con accion + candidatos -> POST /webhook/purchase/match
 *                       (PANEL-COMPRAS-OCR-MATCH-IN-ACTION-AREA-001)
 *
 * Datos reales via ComprasApiClient (NexoAPI v1.6.18+, post T1P PANEL-COMPRAS-REAL-API-MIGRATION-EDIT-V1-001).
 *
 * @module presentation/views/ComprasView
 */

import { ComprasApiClient } from '../../infrastructure/api/ComprasApiClient.js';
import { InsumosApiClient } from '../../infrastructure/api/InsumosApiClient.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { InventarioApiClient } from '../../infrastructure/api/InventarioApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { getOperador } from '../components/OperadorPin.js';
import { AppConfig } from '../../config.js';
import { errorMessage } from '../../domain/value-objects/CrudResult.js';
import { parsearNumeroColombiano, formatearCOP } from '../shared/numeroColombiano.js';
import { avisoDelItem } from '../../application/inventario/requiereProcesamiento.js';
import { createActionArea } from '../components/ActionArea.js';
import { normalizeInsumoConfigFromApi } from '../components/SubEstadosConfig.js';
import { rubroDeRevision } from '../../application/compras/ajusteItemFactura.js';
import { avisar } from '../shared/nxDialogo.js';
import { crearBuscador } from '../shared/buscadorServidor.js';
import { itemResuelto, marcadoSinInventario, sugerirSinInventario, estaCasado, conteoResueltos, cambioSinInventario, textoErrorSinInventario, rechazoDeAprobar } from '../../application/compras/sinInventario.js';

// RB-T3b (bit #3124): no existe GET puntual para saber si un proveedor YA tiene
// rubro asignado (solo PATCH de escritura + GET catalogo). La memoria REAL vive
// en BD (proveedor.rubro_id, los egresos futuros la heredan por COALESCE en el
// backend) -- este cache local solo evita re-preguntar en el MISMO browser tras
// un PATCH exitoso.
function getRubroCache(businessId) {
  try { return JSON.parse(localStorage.getItem(`nx_proveedor_rubro_${businessId}`) || '{}'); }
  catch { return {}; }
}
function setRubroCache(businessId, proveedorId, rubroId) {
  const cache = getRubroCache(businessId);
  cache[proveedorId] = rubroId;
  try { localStorage.setItem(`nx_proveedor_rubro_${businessId}`, JSON.stringify(cache)); } catch { /* quota */ }
}

/**
 * PORC-P2 (bit #3153/#3157): clasificacion CLIENT-SIDE replicada exactamente
 * de la fn v6 de Argy-A (comentario de la migracion 2026_07_17_003, bit #3156):
 *   (a) calculable: unidad de peso + sub_estados_config tiene estado tipo=porcionado
 *       con tamano>0 -> round(gramos/tamano) porciones.
 *   (b) pendiente/alerta: unidad de peso + config enabled SIN estado porcionado
 *       con tamano estructurado.
 *   (c) directo: unidad=UND o insumo sin config -> flujo normal (N unidades).
 * No llama a ningun endpoint nuevo: reusa el mismo GET insumos-con-config que ya
 * consume InventarioView (InventarioApiClient.getInsumosWithConfig).
 */
function calcularDestinoPorcionado(item, insumosConfigByName) {
  const unidad = String(item.unidad || '').trim().toLowerCase();
  const esPeso = unidad !== '' && !['und', 'unidad', 'unidades'].includes(unidad);
  if (!esPeso) return { destino: 'directo' };
  const cfg = insumosConfigByName?.get(String(item.insumo_nombre || item.producto || '').trim().toLowerCase());
  if (!cfg) return { destino: 'directo' };
  const porcEstado = (cfg.estados || []).find(e => e.tipo === 'porcionado' && e.tamano > 0);
  if (!porcEstado) return { destino: 'pendiente' };
  const gramos = Number(item.cantidad) || 0;
  return { destino: 'porciones', porciones: Math.round(gramos / porcEstado.tamano), tamano: porcEstado.tamano };
}

function renderDestinoBadge(item, insumosConfigByName) {
  if (item.match_status !== 'MATCH' || !insumosConfigByName) return '';
  const d = calcularDestinoPorcionado(item, insumosConfigByName);
  if (d.destino === 'porciones') {
    return `<div style="font-size:0.875rem;color:#a371f7;margin-top:2px">→ ${d.porciones} porciones (${d.tamano}g c/u)</div>`;
  }
  if (d.destino === 'pendiente') {
    return `<div style="font-size:0.875rem;color:#d29922;margin-top:2px">→ quedará pendiente de porcionado</div>`;
  }
  return '';
}

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

// OCR-T1 (bit #5260): antes maximumFractionDigits:0 ESCONDIA los centavos en
// todas partes y el usuario no podia ver por que una cifra no cuadraba. Ahora
// un valor entero se muestra limpio y uno con fraccion muestra sus 2 decimales
// — los centavos aparecen exactamente donde existen.
const formatCurrency = (n) => formatearCOP(Number(n) || 0);

/**
 * Devuelve `value` escapado o un fallback consistente cuando es null/undefined/string vacio.
 * B8-P refuerzo: cubrir tambien literal strings 'undefined' / 'null' / 'NaN' que vienen
 * del backend cuando la serializacion deserializa mal (caso bug smoke COCA COLA).
 * Centraliza el patron `escHtml(x || '—')` aplicado en todas las celdas de la vista.
 */
const formatNullable = (value, fallback = '—') => {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  if (s === '' || s === 'undefined' || s === 'null' || s === 'NaN') return fallback;
  return escHtml(s);
};

/**
 * Convierte un timestamp ISO UTC a fecha+hora local Colombia (America/Bogota UTC-5).
 * Usado para diferenciar `created_at` (registro real, mostrado local) de `hora_factura`
 * que viene del PDF (hora del documento, no del registro). B8-P fix.
 */
const formatLocalTime = (isoOrTimestamp) => {
  if (!isoOrTimestamp) return '';
  try {
    const d = new Date(isoOrTimestamp);
    if (isNaN(d.getTime())) return '';
    return formatearFecha(d, {
      timeZone: 'America/Bogota',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }, { parte: 'todo' });
  } catch { return ''; }
};

const TABS = [
  { id: 'facturas',     label: 'Facturas',         icon: '\u{1F4C4}' },
  { id: 'detalle',      label: 'Detalle factura',  icon: '\u{1F50D}' },
  { id: 'movimiento',   label: 'Movimiento manual',icon: '\u{1F4DD}' },
  { id: 'aprobaciones', label: 'Aprobaciones',     icon: '✨' },
  // MV-T5 (bit #3391): fase 2 del canon -- cuanto se desperdicia normalmente por
  // insumo, cuanto rinde al procesarse, y que se salio de lo normal.
  { id: 'estadisticas', label: 'Estadísticas',     icon: '\u{1F4CA}' }
];

const MATCH_FILTER_OPTS = [
  { code: '',         label: 'Todos los match' },
  { code: 'PENDING',  label: 'Pendiente' },
  { code: 'MATCH',    label: 'Matcheada' },
  { code: 'REVIEW',   label: 'Revisar' }
];

// MV-T2 (bit #3349, canon Josse 21-jul, MOV-VOCABULARIO-DOMINIO-V1-001):
// DESPERDICIO y AUMENTO son codes canonicos de primera clase -- la palabra "Merma"
// no debe aparecer en ningun lado y 'ajuste' queda absorbido por 'aumento'
// (unificacion aprobada: el "ajuste" del form manual ES el mismo concepto que el
// "aumento" del kiosk: variacion positiva del insumo al procesarse, ej. arroz que
// sube de peso -- DISTINTO de 'entrada', que es compra via factura).
// - Vocabulario adaptativo (MOV-V3, feedback E2E Josse 2026-07-05) se conserva:
//   "Desperdicio" en negocios de comida, "Pérdida" en tienda/almacen. Solo cambia
//   el label; el code siempre es 'desperdicio'.
// - El historico en BD conserva filas MERMA/AJUSTE: filtros y badges las aceptan
//   como legacy y las muestran siempre como Desperdicio/Aumento (LEGACY_TIPO_EQUIV).
// IMPORTANTE (patron RB-T3b): estos codes SOLO funcionan con nexo-api MV-T1
// (whitelist DESPERDICIO+AUMENTO sin normalizar a ENTRADA) mergeado Y desplegado
// en produccion -- de lo contrario el POST reproduce el 400. Merge: API primero.
const FOOD_BUSINESS_RE = /restaurante|comida|cafeter|panader|\bbar\b/i;
const isFoodBusiness = (businessType) => FOOD_BUSINESS_RE.test(String(businessType || ''));

// code canonico -> code legacy equivalente del historico (solo lectura/filtros).
const LEGACY_TIPO_EQUIV = { desperdicio: 'merma', aumento: 'ajuste' };

function tipoMovOpts(businessType) {
  return [
    { code: 'entrada',     label: 'Entrada (compra/ingreso)' },
    { code: 'desperdicio', label: isFoodBusiness(businessType) ? 'Desperdicio' : 'Pérdida' },
    { code: 'dano',        label: 'Daño' },
    { code: 'devolucion',  label: 'Devolución' },
    { code: 'aumento',     label: 'Aumento' }
  ];
}
const tipoFilterOpts = (businessType) => [
  { code: '', label: 'Todos los tipos' },
  ...tipoMovOpts(businessType)
];

export async function renderCompras(container) {
  const state = {
    activeTab: 'facturas',
    facturas: [],
    facturasTotales: { totalFacturas: 0, countByMatch: { PENDING: 0, MATCH: 0, REVIEW: 0 } },
    facturasFilters: { match: '', proveedor: '', from: '', to: '' },
    facturasLoading: false,
    selectedFacturaId: null,
    selectedFacturaDetail: null,
    rubros: [],            // RB-T3b: catalogo lazy-load (Detalle tab)
    rubrosLoadedFor: null, // businessId cacheado, evita refetch en cada render
    insumosConfigByName: null,   // PORC-P2: Map nombre_lower -> config sub_estados (lazy-load)
    insumosConfigLoadedFor: null,
    movimientos: [],
    movimientosFilters: { tipo: '' },
    movimientosLoading: false,
    movimientoForm: {
      tipo: 'desperdicio',
      insumoId: '',                 // PANEL-MOVIMIENTO-FORM-INSUMO-CANTIDAD-V1-001: backend lo exige
      referencia: '',
      fecha: new Date().toISOString().slice(0, 10),
      cantidad: 1,                  // reemplaza items_count: es la cantidad del insumo afectado
      total: 0,
      motivo: ''
    },
    movimientoInsumos: [],          // catalogo de insumos para el dropdown del form
    catalogoInsumosCompleto: false, // T169 pieza B: Estadisticas cargo el catalogo entero (movimientoInsumos tambien guarda lo buscado)
    estadisticas: null,             // MV-T5: respuesta del endpoint de estadisticas
    estadisticasFilters: { semanas: 8 },
    movBusinessType: '',            // MOV-V3: business_type del negocio (vocabulario adaptativo)
    movBizTypeFor: null,            // businessId al que corresponde movBusinessType (cache)
    actionArea: null
  };

  container.innerHTML = `
    <section class="artifact-view compras-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>\u{1F6D2} Compras &amp; Movimientos</h1>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Facturas de proveedor + matcheo al inventario + movimientos manuales (desperdicios/daños/devoluciones)
          </p>
        </div>
      </div>

      <nav data-cv-tabs class="compras-tabs"
           style="display:flex;gap:4px;margin-top:14px;border-bottom:1px solid #30363d;flex-wrap:wrap">
        ${TABS.map(t => `
          <button type="button" data-cv-tab="${t.id}" class="compras-tab"
                  style="padding:8px 14px;background:transparent;border:none;border-bottom:2px solid transparent;
                         color:#8b949e;cursor:pointer;font-size:0.875rem;display:flex;align-items:center;gap:6px">
            ${t.icon} ${escHtml(t.label)}
          </button>
        `).join('')}
      </nav>

      <div data-cv-tab-body style="margin-top:14px"></div>
    </section>
  `;

  const $tabs = container.querySelector('[data-cv-tabs]');
  const $body = container.querySelector('[data-cv-tab-body]');

  $tabs.addEventListener('click', (e) => {
    const $btn = e.target.closest('[data-cv-tab]');
    if (!$btn) return;
    state.activeTab = $btn.dataset.cvTab;
    renderActiveTab();
  });

  await renderActiveTab();

  // ============== Tab routing ==============

  async function renderActiveTab() {
    updateTabsActive();
    switch (state.activeTab) {
      case 'facturas':     return renderFacturasTab();
      case 'detalle':      return renderDetalleTab();
      case 'movimiento':   return renderMovimientoTab();
      case 'aprobaciones': return renderAprobacionesTab();
      case 'estadisticas': return renderEstadisticasTab();
    }
  }

  function updateTabsActive() {
    container.querySelectorAll('[data-cv-tab]').forEach(btn => {
      const active = btn.dataset.cvTab === state.activeTab;
      btn.style.color = active ? '#58a6ff' : '#8b949e';
      btn.style.borderBottomColor = active ? '#58a6ff' : 'transparent';
      btn.style.fontWeight = active ? '600' : '400';
    });
  }

  // ============== Tab 1: Facturas ==============

  async function renderFacturasTab() {
    $body.innerHTML = `
      <form data-cv-fac-filters class="compras-filters"
            style="display:flex;flex-direction:column;gap:8px;
                   padding:10px 12px;background:#161b22;border:1px solid #30363d;border-radius:8px">
        <!-- T2 contenedores (PANEL-MISC-POLISH-V1-001): 4 columnas fijas con inputs de min-width 120px pedian 4x120+gaps
             en un telefono de 375 y empujaban la PAGINA. auto-fit baja a 2 columnas en telefono y queda en 4 en escritorio. -->
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px">
          <label style="display:flex;flex-direction:column;gap:3px;font-size:0.875rem;color:#8b949e">
            Match
            <select name="match" class="form-input form-input-sm">
              ${MATCH_FILTER_OPTS.map(o => `<option value="${o.code}" ${o.code === state.facturasFilters.match ? 'selected' : ''}>${o.label}</option>`).join('')}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:3px;font-size:0.875rem;color:#8b949e">
            Proveedor
            <input type="text" name="proveedor" class="form-input form-input-sm" placeholder="contiene&hellip;" value="${escHtml(state.facturasFilters.proveedor)}"/>
          </label>
          <label style="display:flex;flex-direction:column;gap:3px;font-size:0.875rem;color:#8b949e">
            Desde
            <input type="date" name="from" class="form-input form-input-sm" value="${escHtml(state.facturasFilters.from)}"/>
          </label>
          <label style="display:flex;flex-direction:column;gap:3px;font-size:0.875rem;color:#8b949e">
            Hasta
            <input type="date" name="to" class="form-input form-input-sm" value="${escHtml(state.facturasFilters.to)}"/>
          </label>
        </div>
        <div style="display:flex;gap:6px;justify-content:flex-end">
          <button type="button" data-cv-fac-reset class="btn-ghost btn-sm">Limpiar</button>
          <button type="submit" class="btn-primary btn-sm">Aplicar</button>
        </div>
      </form>

      <div data-cv-fac-counters style="display:flex;gap:10px;margin-top:12px;flex-wrap:wrap"></div>

      <div data-cv-fac-tablewrap style="margin-top:14px;border:1px solid #30363d;border-radius:8px;overflow-x:auto;background:#0d1117">
        <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
          <thead style="background:#161b22">
            <tr>
              ${['Fecha', 'Hora', 'Proveedor', 'Numero', 'Items', 'Match', 'Total', ''].map(h => `
                <th style="padding:8px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">${escHtml(h)}</th>
              `).join('')}
            </tr>
          </thead>
          <tbody data-cv-fac-tbody></tbody>
        </table>
      </div>
    `;

    const $form = $body.querySelector('[data-cv-fac-filters]');
    const $reset = $body.querySelector('[data-cv-fac-reset]');
    $form.addEventListener('submit', (e) => {
      e.preventDefault();
      state.facturasFilters = {
        match:     $form.querySelector('[name="match"]').value,
        proveedor: $form.querySelector('[name="proveedor"]').value,
        from:      $form.querySelector('[name="from"]').value,
        to:        $form.querySelector('[name="to"]').value
      };
      loadFacturas();
    });
    $reset.addEventListener('click', () => {
      state.facturasFilters = { match: '', proveedor: '', from: '', to: '' };
      renderFacturasTab();
    });

    await loadFacturas();
  }

  async function loadFacturas() {
    const businessId = getSelectedBusiness();
    const $tbody = $body.querySelector('[data-cv-fac-tbody]');
    const $counters = $body.querySelector('[data-cv-fac-counters]');
    if (!businessId) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:20px;text-align:center;color:#d29922">Selecciona un negocio en la barra superior</td></tr>`;
      return;
    }
    state.facturasLoading = true;
    $tbody.innerHTML = `<tr><td colspan="8" style="padding:20px;text-align:center;color:#8b949e">&#x21BB; cargando&hellip;</td></tr>`;
    const res = await ComprasApiClient.listFacturas(businessId, state.facturasFilters);
    state.facturasLoading = false;
    if (!res.ok) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:20px;text-align:center;color:#f85149">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 120))}</td></tr>`;
      return;
    }
    const payload = res.data || {};
    state.facturas = payload.items || [];
    state.facturasTotales = payload.totales || { totalFacturas: 0, countByMatch: { PENDING: 0, MATCH: 0, REVIEW: 0 } };
    renderFacturasRows();
    renderFacturasCounters();
  }

  function renderFacturasRows() {
    const $tbody = $body.querySelector('[data-cv-fac-tbody]');
    if (state.facturas.length === 0) {
      $tbody.innerHTML = `<tr><td colspan="8" style="padding:24px;text-align:center;color:#8b949e">
        Sin facturas para los filtros aplicados.
      </td></tr>`;
      return;
    }
    $tbody.innerHTML = state.facturas.map(f => {
      const match = ComprasApiClient.matchStatusMeta(f.match_status);
      // FV-T5: chip de verificacion pendiente en el listado, para que el admin vea
      // sin entrar cuales facturas esperan al colaborador. Null-safe: si el backend
      // no manda verificacion_status (o ya esta APPROVED), no se pinta nada.
      const verif = ComprasApiClient.verificacionMeta(f.verificacion_status);
      const verifChip = (verif && f.verificacion_status === 'PENDING')
        ? `<span title="${escHtml(verif.desc)}" style="margin-left:5px;background:${verif.color}22;color:${verif.color};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:600;white-space:nowrap">${verif.icon} ${escHtml(verif.label)}</span>`
        : '';
      return `
        <tr data-cv-fac-row="${escHtml(f.id)}"
            style="cursor:pointer;border-bottom:1px solid #21262d"
            onmouseover="this.style.background='#161b22'"
            onmouseout="this.style.background='transparent'">
          <td style="padding:8px 10px;color:#c9d1d9">${formatNullable(f.fecha)}</td>
          <td style="padding:8px 10px;color:#c9d1d9">${formatNullable(f.hora)}</td>
          <td style="padding:8px 10px;color:#c9d1d9">${formatNullable(f.proveedor)}</td>
          <td style="padding:8px 10px;color:#79c0ff;font-family:monospace">${formatNullable(f.numero)}</td>
          <td style="padding:8px 10px;color:#c9d1d9">${f.items_count}</td>
          <td style="padding:8px 10px;white-space:nowrap">
            <span title="${escHtml(match.desc)}"
                  style="background:${match.color}22;color:${match.color};padding:2px 10px;border-radius:10px;font-size:0.875rem;font-weight:600">
              ${escHtml(match.label)}
            </span>${verifChip}
          </td>
          <td style="padding:8px 10px;text-align:right;color:#3fb950;font-weight:600">$${formatCurrency(f.total)}</td>
          <td style="padding:8px 10px;text-align:right">
            <button type="button" data-cv-fac-detail="${escHtml(f.id)}" class="btn-ghost btn-sm" style="font-size:0.875rem">Ver detalle &rarr;</button>
          </td>
        </tr>
      `;
    }).join('');

    $tbody.querySelectorAll('[data-cv-fac-detail]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        state.selectedFacturaId = btn.dataset.cvFacDetail;
        state.selectedFacturaDetail = null;
        state.activeTab = 'detalle';
        renderActiveTab();
      });
    });
    $tbody.querySelectorAll('[data-cv-fac-row]').forEach(tr => {
      tr.addEventListener('click', () => {
        state.selectedFacturaId = tr.dataset.cvFacRow;
        state.selectedFacturaDetail = null;
        state.activeTab = 'detalle';
        renderActiveTab();
      });
    });
  }

  function renderFacturasCounters() {
    const $c = $body.querySelector('[data-cv-fac-counters]');
    const c = state.facturasTotales.countByMatch || {};
    const monto = state.facturasTotales.totalFacturas || 0;
    const totalCount = (c.PENDING || 0) + (c.MATCH || 0) + (c.REVIEW || 0);
    $c.innerHTML = `
      ${counterCard('Total facturas', String(totalCount), '#58a6ff')}
      ${counterCard('Pendientes', String(c.PENDING || 0), '#d29922')}
      ${counterCard('Matcheadas', String(c.MATCH || 0), '#3fb950')}
      ${counterCard('Revisar', String(c.REVIEW || 0), '#f85149')}
      ${counterCardSummary('Total $', '$' + formatCurrency(monto), '#3fb950')}
    `;
  }

  function counterCard(label, value, color) {
    return `
      <div style="flex:0 0 auto;min-width:140px;padding:10px 14px;background:#161b22;border:1px solid #30363d;border-left:3px solid ${color};border-radius:6px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${escHtml(label)}</div>
        <div style="font-size:1.05rem;color:${color};font-weight:700;margin-top:2px">${value}</div>
      </div>
    `;
  }

  function counterCardSummary(label, value, color) {
    return `
      <div style="flex:0 0 auto;min-width:160px;margin-left:auto;padding:10px 14px;background:#0d1117;border:1px dashed ${color};border-radius:6px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${escHtml(label)}</div>
        <div style="font-size:1.05rem;color:${color};font-weight:700;margin-top:2px">${value}</div>
      </div>
    `;
  }

  // ============== Tab 2: Detalle factura ==============

  async function renderDetalleTab() {
    if (!state.selectedFacturaId) {
      $body.innerHTML = `
        <div style="padding:32px;text-align:center;color:#8b949e;background:#161b22;border:1px dashed #30363d;border-radius:8px">
          <div style="font-size:2rem;margin-bottom:10px">\u{1F50D}</div>
          <p>Selecciona una factura en la pestana <strong>Facturas</strong> para ver su detalle.</p>
        </div>
      `;
      return;
    }
    const businessId = getSelectedBusiness();
    if (!state.selectedFacturaDetail) {
      $body.innerHTML = `<div style="padding:30px;text-align:center;color:#8b949e">&#x21BB; cargando detalle&hellip;</div>`;
      const res = await ComprasApiClient.getFacturaById(businessId, state.selectedFacturaId);
      if (!res.ok) {
        $body.innerHTML = `<div style="padding:30px;color:#f85149">Error: ${escHtml(JSON.stringify(res.error).slice(0, 200))}</div>`;
        return;
      }
      state.selectedFacturaDetail = res.data;
    }
    const f = state.selectedFacturaDetail;

    // RB-T3b (bit #3124): catalogo de rubros lazy-load, una vez por businessId.
    if (f.proveedor_id && state.rubrosLoadedFor !== businessId) {
      const rres = await FinanzasApiClient.listRubrosEgreso(businessId);
      state.rubros = rres.ok ? rres.items : [];
      state.rubrosLoadedFor = businessId;
    }

    // PORC-P2 (bit #3153): config sub_estados lazy-load, una vez por businessId.
    if (state.insumosConfigLoadedFor !== businessId) {
      const cfgRes = await InventarioApiClient.getInsumosWithConfig(businessId);
      const map = new Map();
      if (cfgRes.ok && Array.isArray(cfgRes.data)) {
        for (const ins of cfgRes.data) {
          if (!ins?.nombre) continue;
          const norm = normalizeInsumoConfigFromApi(ins);
          if (norm) map.set(String(ins.nombre).trim().toLowerCase(), norm);
        }
      }
      state.insumosConfigByName = map;
      state.insumosConfigLoadedFor = businessId;
    }

    const match = ComprasApiClient.matchStatusMeta(f.match_status);
    // FV-T5 (bit #3355): DOBLE GATE. Desktop descarga la factura solo si (a) todos
    // los items estan en MATCH y (b) un colaborador verifico cantidades/empaques.
    // verificacion_status es null mientras FV-T1/FV-T2 no esten desplegados: en ese
    // caso el gate se comporta EXACTAMENTE como antes (solo match), sin badge.
    const verifMeta = ComprasApiClient.verificacionMeta(f.verificacion_status);
    const verifPendiente = f.verificacion_status === 'PENDING';
    // F5-P: derivar estado confirmacion de match_status (MATCH = todos items match -> egreso confirmed por backend hook).
    // COMPRAS-BORRADOR-ANULACION-T1: resuelto = casado a un insumo o marcado sin inventario por una persona (contrato
    // v0.2 de A). El egreso se confirma con esa misma regla en el trigger de la API.
    const cuenta = conteoResueltos(f.items);
    const matchCompleto = cuenta.total > 0 ? cuenta.resueltos === cuenta.total : f.match_status === 'MATCH';
    const isConfirmed = matchCompleto && !verifPendiente;
    const itemsNoMatch = (f.items || []).filter(it => !itemResuelto(it));
    const itemsAutoConfirmable = itemsNoMatch.filter(it => (it.candidatos || []).some(c => (c.similitud || 0) >= 0.30));
    // Que falta exactamente para que Desktop pueda descargarla (NSS-4: el usuario
    // no debe adivinar cual de los dos gates lo esta frenando).
    const faltantes = [
      !matchCompleto ? `items resueltos: con insumo o sin inventario (${cuenta.resueltos}/${cuenta.total})` : null,
      verifPendiente ? 'verificacion del colaborador (cantidades y factor de empaque)' : null
    ].filter(Boolean);
    const confirmBannerHtml = isConfirmed
      ? `<div style="padding:10px 14px;background:#3fb95022;border:1px solid #3fb950;border-radius:6px;color:#3fb950;font-size:0.875rem;display:flex;align-items:center;gap:10px">
           <span style="font-size:1.1rem">✓</span>
           <div>Factura confirmada${verifMeta ? ' y verificada' : ''}: lista para que Nexo Desktop la descargue.</div>
         </div>`
      : `<div data-cv-confirm-banner style="padding:10px 14px;background:#d2992222;border:1px solid #d29922;border-radius:6px;color:#d29922;font-size:0.875rem;display:flex;flex-wrap:wrap;align-items:center;gap:10px;justify-content:space-between">
           <div style="display:flex;align-items:flex-start;gap:8px;flex:1;min-width:240px">
             <span style="font-size:1.1rem">⏳</span>
             <div>
               <strong>Pendiente de confirmacion</strong>
               <div style="font-size:0.875rem;color:#c9d1d9;margin-top:2px">Desktop NO descargara la factura hasta completar: ${escHtml(faltantes.join(' + '))}.</div>
             </div>
           </div>
           ${itemsAutoConfirmable.length > 0
             ? `<button type="button" data-cv-auto-confirm class="btn-primary btn-sm" style="font-size:0.875rem;white-space:nowrap">✓ Confirmar todos (auto-match)</button>`
             : matchCompleto && verifPendiente
               ? `<div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">
                    <button type="button" data-cv-aprobar-verif data-confirm="0" class="btn-primary btn-sm"
                            title="Aprueba la verificacion desde el panel (equivale al aprobar del kiosk; queda registrada a tu nombre). Revisa los items abajo antes."
                            style="font-size:0.875rem;white-space:nowrap">✓ Aprobar verificacion (admin)</button>
                    <span style="font-size:0.875rem;color:#8b949e;font-style:italic">o el colaborador la verifica desde el kiosk</span>
                  </div>`
               : `<span style="font-size:0.875rem;color:#8b949e;font-style:italic">${matchCompleto ? 'Match completo: falta que el colaborador verifique desde el kiosk.' : 'Items sin candidatos: requieren match manual via Editar.'}</span>`
           }
         </div>`;
    $body.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:14px">
        ${confirmBannerHtml}
        <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px;
                    padding:14px 16px;background:#161b22;border:1px solid #30363d;border-radius:8px">
          <div>
            <div style="font-size:0.875rem;color:#8b949e">Factura</div>
            <h3 style="margin:4px 0;color:#79c0ff;font-family:monospace;font-size:1.1rem">${formatNullable(f.numero)}</h3>
            <div style="color:#c9d1d9;font-size:0.875rem">${formatNullable(f.proveedor)}</div>
            ${f.proveedor_id ? renderRubroPicker(f.proveedor_id, businessId) : ''}
            <div style="color:#8b949e;font-size:0.875rem;margin-top:3px;display:flex;gap:10px;flex-wrap:wrap">
              <span title="Fecha y hora extraidas del PDF de la factura">📄 PDF: ${formatNullable(f.fecha)}${f.hora ? ' ' + formatNullable(f.hora, '') : ''}</span>
              ${f.created_at ? `<span title="Fecha y hora cuando se registro la factura en Nexo (zona horaria Colombia)" style="color:#79c0ff">🕒 Registrada: ${escHtml(formatLocalTime(f.created_at))}</span>` : ''}
            </div>
          </div>
          <div style="text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:6px">
            <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end">
              <span title="${escHtml(match.desc)}"
                    style="background:${match.color}22;color:${match.color};padding:4px 12px;border-radius:12px;font-size:0.875rem;font-weight:600">
                ${escHtml(match.label)}
              </span>
              ${verifMeta
                ? `<span title="${escHtml(verifMeta.desc)}"
                         style="background:${verifMeta.color}22;color:${verifMeta.color};padding:4px 12px;border-radius:12px;font-size:0.875rem;font-weight:600">
                     ${verifMeta.icon} ${escHtml(verifMeta.label)}
                   </span>`
                : ''}
            </div>
            <div style="font-size:1.3rem;color:#3fb950;font-weight:700">$${formatCurrency(f.total)}</div>
            <div style="display:flex;gap:4px;flex-wrap:wrap;justify-content:flex-end">
              <button type="button" data-cv-edit-factura class="btn-ghost btn-sm"
                      style="font-size:0.875rem;padding:3px 10px">✏ Editar header</button>
              <button type="button" data-cv-del-factura class="btn-ghost btn-sm"
                      title="Eliminar factura completa" style="font-size:0.875rem;padding:3px 10px;color:#f85149">🗑 Eliminar</button>
            </div>
          </div>
        </div>

        <div style="border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
          <div style="padding:10px 14px;background:#161b22;font-size:0.875rem;color:#f0f6fc;border-bottom:1px solid #30363d">
            \u{1F4E6} Items de la factura (${(f.items || []).length})
          </div>
          ${(f.items || []).length === 0
            ? `<div style="padding:18px;text-align:center;color:#8b949e">Sin items detallados para esta factura.</div>`
            : `<div data-cv-items-scroll style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:0.875rem">
                <thead><tr>
                  <th style="padding:6px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Producto</th>
                  <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Cant.</th>
                  <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Unit.</th>
                  <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Subtotal</th>
                  <th style="padding:6px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Match</th>
                  <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Acciones</th>
                </tr></thead>
                <tbody>
                  ${(f.items || []).map(it => renderItemRow(it, state.insumosConfigByName)).join('')}
                </tbody>
              </table></div>`
          }
        </div>

        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" data-cv-detalle-back class="btn-ghost btn-sm">&larr; Volver a facturas</button>
        </div>
      </div>
    `;
    $body.querySelector('[data-cv-detalle-back]').addEventListener('click', () => {
      state.activeTab = 'facturas';
      renderActiveTab();
    });
    $body.querySelector('[data-cv-edit-factura]')?.addEventListener('click', () => {
      openEditarFacturaModal(f);
    });
    $body.querySelector('[data-cv-del-factura]')?.addEventListener('click', () => {
      confirmDeleteFacturaModal(f);
    });
    $body.querySelector('[data-cv-auto-confirm]')?.addEventListener('click', () => {
      autoConfirmAllItems(f);
    });
    // OCR-FACT-REVIEW: aprobar verificacion desde el panel manager. verificarFactura()
    // existia en el ApiClient y NADIE lo llamaba desde esta vista: si el colaborador no
    // estaba, la factura quedaba bloqueada sin salida. Doble click como confirmacion
    // (patron data-confirm, nada de confirm() nativo).
    const $aprobarBtn = $body.querySelector('[data-cv-aprobar-verif]');
    $aprobarBtn?.addEventListener('click', async () => {
      if ($aprobarBtn.dataset.confirm === '0') {
        $aprobarBtn.dataset.confirm = '1';
        $aprobarBtn.textContent = '¿Confirmar aprobacion?';
        setTimeout(() => {
          if ($aprobarBtn.isConnected && $aprobarBtn.dataset.confirm === '1') {
            $aprobarBtn.dataset.confirm = '0';
            $aprobarBtn.textContent = '✓ Aprobar verificacion (admin)';
          }
        }, 4000);
        return;
      }
      $aprobarBtn.disabled = true;
      $aprobarBtn.textContent = 'Aprobando…';
      const vres = await ComprasApiClient.verificarFactura(businessId, f.id, {
        aprobar: true,
        verificadoPor: AppConfig.userName || AppConfig.role || 'admin-panel'
      });
      if (vres.ok) {
        state.selectedFacturaDetail = null;
        renderDetalleTab();
      } else {
        $aprobarBtn.disabled = false;
        $aprobarBtn.dataset.confirm = '0';
        $aprobarBtn.textContent = '✓ Aprobar verificacion (admin)';
        // Follow-up review #4443: errorMessage() (MV-T2b) en vez del error generico.
        const msg = errorMessage(vres, 'no se pudo aprobar la verificacion');
        const $banner = $body.querySelector('[data-cv-confirm-banner]');
        // nexo-api 578 (D-139): ITEMS_SIN_RESOLVER trae los items que faltan; FACTURA_ANULADA no se aprueba.
        const rechazo = rechazoDeAprobar(vres);
        const detalle = rechazo.codigo === 'ITEMS_SIN_RESOLVER'
          ? `Faltan ${rechazo.items.length || 'algunos'} item(s) por resolver: ${rechazo.items.map(i => escHtml(i.descripcion || 'sin nombre')).join(', ')}. A cada uno ponle su insumo (Editar) o marca Sin inventario.`
          : rechazo.codigo === 'FACTURA_ANULADA' ? 'La factura esta anulada: ya no se aprueba.'
          : `No se pudo aprobar: ${escHtml(String(msg).slice(0, 160))}`;
        if ($banner) $banner.insertAdjacentHTML('beforeend',
          `<div data-cv-aprobar-rechazo style="width:100%;font-size:14px;color:#f85149">${detalle}</div>`);
      }
    });
    // COMPRAS-BORRADOR-ANULACION-T1: la casilla Sin inventario guarda en el acto (PATCH del item) y relee la factura.
    $body.querySelectorAll('[data-cv-sin-inventario]').forEach($chk => {
      $chk.addEventListener('change', async () => {
        const item = (f.items || []).find(i => i.id === $chk.dataset.cvSinInventario);
        const $msg = $body.querySelector(`[data-cv-sin-inventario-msg="${CSS.escape($chk.dataset.cvSinInventario)}"]`);
        const c = cambioSinInventario(item, $chk.checked);
        if (c.error) { $chk.checked = marcadoSinInventario(item); if ($msg) $msg.textContent = textoErrorSinInventario(c.error); return; }
        $chk.disabled = true;
        if ($msg) $msg.textContent = 'Guardando…';
        const res = await ComprasApiClient.updateItem(businessId, f.id, item.id, c.patch);
        if (!res.ok) {
          $chk.disabled = false;
          $chk.checked = marcadoSinInventario(item);
          if ($msg) $msg.textContent = textoErrorSinInventario(errorMessage(res, ''));
          return;
        }
        state.selectedFacturaDetail = null;
        renderDetalleTab();
      });
    });
    $body.querySelectorAll('[data-cv-edit-item]').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemId = btn.dataset.cvEditItem;
        const item = (f.items || []).find(i => i.id === itemId);
        if (item) openEditarItemModal(item, f.id);
      });
    });

    // RB-T3b: asignar/cambiar rubro del proveedor -> PATCH + memoria local.
    const $rubroSel = $body.querySelector('[data-cv-rubro-select]');
    $rubroSel?.addEventListener('change', async () => {
      const $status = $body.querySelector('[data-cv-rubro-status]');
      const rubroId = $rubroSel.value ? Number($rubroSel.value) : null;
      $rubroSel.disabled = true;
      if ($status) $status.textContent = 'Guardando…';
      const res = await FinanzasApiClient.setProveedorRubro(businessId, f.proveedor_id, rubroId);
      $rubroSel.disabled = false;
      if (res.ok) {
        setRubroCache(businessId, f.proveedor_id, rubroId);
        if ($status) { $status.textContent = '✓ Guardado'; $status.style.color = '#3fb950'; }
      } else {
        if ($status) { $status.textContent = 'Error al guardar'; $status.style.color = '#f85149'; }
      }
    });
  }

  /**
   * RB-T3b (bit #3124): dropdown inline para asignar el rubro del proveedor de
   * la factura en revision. El PATCH persiste en BD (proveedor.rubro_id) --
   * "memoria" para que egresos futuros de ese proveedor hereden el rubro sin
   * volver a preguntar (COALESCE en backend). El cache local (getRubroCache)
   * solo evita re-preguntar en el MISMO browser tras un guardado exitoso, ya
   * que no existe GET puntual para saber el estado actual del proveedor.
   */
  function renderRubroPicker(proveedorId, businessId) {
    if (!state.rubros.length) return '';
    const cached = getRubroCache(businessId)[proveedorId];
    const opts = `<option value="">— sin rubro —</option>` + state.rubros.map(r =>
      `<option value="${escHtml(r.id)}" ${String(cached) === String(r.id) ? 'selected' : ''}>${escHtml(r.nombre)}</option>`).join('');
    return `
      <div style="margin-top:6px;display:flex;align-items:center;gap:6px;font-size:0.875rem">
        <span style="color:#8b949e">🏷️ Rubro:</span>
        <select data-cv-rubro-select class="form-input form-input-sm" style="font-size:0.875rem;padding:2px 6px">${opts}</select>
        <span data-cv-rubro-status style="font-size:0.875rem;color:#8b949e"></span>
      </div>`;
  }

  // FV-T5 (bit #3355): pista bajo la cantidad -- "5 caja x8" explica de donde salen
  // las 40 unidades que entran al inventario (caso Badur 1318625).
  // El factor NO siempre es "unidades por caja": tambien cubre conversion de unidad
  // (evidencia FV-T1 bit #3360, caso camaron: 4 KG en la factura -> 4000 g de stock,
  // factor 1000). Por eso se pinta con cualquier factor != 1 (incluido fraccionario,
  // ej. factura en gramos e insumo en kilos) y el tooltip habla de "equivale a".
  // Devuelve '' si el backend aun no manda los campos (null-safe) o si factor === 1:
  // sin conversion no hay nada que explicar y la fila queda como hoy.
  function renderEmpaqueHint(it) {
    const cantFactura = Number(it.cantidad_factura);
    const factor = Number(it.factor_empaque);
    if (!(cantFactura > 0) || !(factor > 0) || factor === 1) return '';
    const unidad = it.unidad_factura ? String(it.unidad_factura) : '';
    const enFactura = `${cantFactura}${unidad ? ' ' + unidad : ''}`;
    return `<div style="font-size:0.875rem;color:#8b949e;margin-top:2px;white-space:nowrap"
                 title="La factura dice ${escHtml(enFactura)}; equivale a ${escHtml(String(it.cantidad ?? ''))} en el inventario (factor ${escHtml(String(factor))})">
              ${escHtml(enFactura)} &times;${escHtml(String(factor))}
            </div>`;
  }

  /** COMPRAS-BORRADOR-ANULACION-T1: casilla Sin inventario de un item sin insumo (un item casado no la lleva). */
  function renderSinInventario(it) {
    if (estaCasado(it)) return '';
    const marcado = marcadoSinInventario(it);
    const sugerido = sugerirSinInventario(it)
      ? `<div style="font-size:14px;color:#d29922">Parece un flete o un servicio. Si no entra al inventario, marca la casilla.</div>`
      : '';
    return `<div style="margin-top:4px">
        <label style="display:inline-flex;align-items:center;gap:6px;font-size:14px;color:#c9d1d9;cursor:pointer;white-space:nowrap">
          <input type="checkbox" data-cv-sin-inventario="${escHtml(it.id)}" ${marcado ? 'checked' : ''}> Sin inventario
        </label>${sugerido}
        <div data-cv-sin-inventario-msg="${escHtml(it.id)}" role="status" style="font-size:14px;color:#f85149"></div>
      </div>`;
  }

  // AL-T1 (nexo-api 654): el insumo casado se procesa al llegar. Solo si el item no esta marcado sin inventario.
  function renderAvisoProcesar(it) {
    const aviso = marcadoSinInventario(it) ? null : avisoDelItem(it);
    return aviso ? `<div data-cv-aviso-procesar="${escHtml(it.id)}" role="note" style="margin-top:4px;font-size:0.875rem;color:#d29922">⚠ ${escHtml(aviso)}</div>` : '';
  }

  function renderItemRow(it, insumosConfigByName) {
    const candidatos = it.candidatos || [];
    const maxSim = candidatos.length > 0
      ? Math.max(...candidatos.map(c => Number(c.similitud) || 0))
      : 0;
    let matchInfo;
    // B5-P fix: priorizar match_status=MATCH antes de chequear candidatos.
    // Bug previo: items ya MATCH en BD se renderizaban como "Sin candidatos" porque la
    // logica solo miraba candidatos[].length sin considerar el estado actual del match.
    if (marcadoSinInventario(it)) {
      matchInfo = `<span data-cv-item-sin-inventario style="color:#79c0ff;font-size:14px">No entra al inventario (flete o servicio)</span>`;
    } else if (it.match_status === 'MATCH' && (it.insumo_id || it.insumo_nombre)) {
      const score = it.match_score || maxSim;
      const nombre = it.insumo_nombre || (candidatos.find(c => c.producto_existente_id === it.insumo_id)?.nombre) || 'matcheado';
      matchInfo = `<span style="color:#3fb950;font-size:0.875rem">✓ ${formatNullable(nombre)}${score ? ` (${Math.round(score * 100)}%)` : ''}</span>`;
    } else if (candidatos.length === 0) {
      matchInfo = `<span style="color:#f85149;font-size:0.875rem">Sin candidatos</span>`;
    } else if (maxSim < 0.30) {
      matchInfo = `<span title="El LLM encontro nombres parecidos pero similitud muy baja" style="color:#d29922;font-size:0.875rem">Sin match confiable</span>`;
    } else if (candidatos.length === 1 && maxSim >= 0.95) {
      matchInfo = `<span style="color:#3fb950;font-size:0.875rem">✓ ${formatNullable(candidatos[0].nombre)} (${Math.round(maxSim * 100)}%)</span>`;
    } else {
      matchInfo = `<span style="color:#d29922;font-size:0.875rem">${candidatos.length} candidato${candidatos.length > 1 ? 's' : ''} (max ${Math.round(maxSim * 100)}%)</span>`;
    }
    return `
      <tr style="border-bottom:1px solid #21262d">
        <td style="padding:6px 10px;color:#c9d1d9">${escHtml(it.producto)}</td>
        <td style="padding:6px 10px;text-align:right;color:#c9d1d9">
          ${escHtml(it.cantidad)}${renderEmpaqueHint(it)}
        </td>
        <td style="padding:6px 10px;text-align:right;color:#c9d1d9">$${formatCurrency(it.precio_unit)}</td>
        <td style="padding:6px 10px;text-align:right;color:#3fb950;font-weight:600">$${formatCurrency(it.subtotal)}</td>
        <td style="padding:6px 10px">${matchInfo}${renderAvisoProcesar(it)}${renderDestinoBadge(it, insumosConfigByName)}${renderSinInventario(it)}</td>
        <td style="padding:6px 10px;text-align:right">
          <button type="button" data-cv-edit-item="${escHtml(it.id)}" class="btn-ghost btn-sm" style="font-size:0.875rem;padding:2px 8px">✏ Editar</button>
        </td>
      </tr>
    `;
  }

  // ============== Tab 3: Movimiento manual ==============

  // MOVM-2 (bit #3022): label 'nombre — unidad' (unidad del insumo canonico); presentacion en medio solo si existe.
  function etiquetaInsumo(i) {
    const label = [i.nombre, i.presentacion].filter(Boolean).join(' ') + (i.unidad ? ` — ${i.unidad}` : '');
    return label.trim() || String(i.insumo_id ?? i.id);
  }

  // MOVM-2 (bit #3022): dedupe defensivo — la re-migracion Desktop duplico insumos en panel ("Coca Cola und" +
  // "Coca Cola unidad"). El sync_uuid NO viaja en este GET, asi que la clave es el nombre normalizado; ante
  // duplicado gana el primero en orden alfabetico.
  function sinInsumosRepetidos(items) {
    const seen = new Map();
    for (const i of items) {
      const key = String(i.nombre_normalizado || i.nombre || i.insumo_id || i.id).trim().toLowerCase();
      if (!seen.has(key)) seen.set(key, i);
    }
    return [...seen.values()].sort((a, b) =>
      String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', { sensitivity: 'base' }));
  }

  // El catalogo completo de insumos: hoy solo lo usa Estadisticas para las unidades (el movimiento manual ya busca
  // en el servidor, T169 pieza B). Marca aparte porque movimientoInsumos tambien guarda lo que el buscador mostro.
  async function loadMovimientoInsumos(businessId) {
    if (!businessId) { state.movimientoInsumos = []; return; }
    // CART-1 (bit #2995): searchAllInsumos pagina de a 100 (tope del backend) hasta traer el catalogo.
    const res = await InsumosApiClient.searchAllInsumos(businessId);
    const items = (res.ok && Array.isArray(res.data?.items)) ? res.data.items : [];
    state.movimientoInsumos = sinInsumosRepetidos(items);
    state.catalogoInsumosCompleto = res.ok;
  }

  async function renderMovimientoTab() {
    $body.innerHTML = `
      <!-- MOV-V3: layout en clase CSS (media query real) — el grid inline anterior dejaba
           el form a ~280px con 3 columnas internas y los campos se recortaban. -->
      <div class="cv-mov-layout">

        <form data-cv-mov-form
              style="display:flex;flex-direction:column;gap:12px;padding:16px;background:#161b22;border:1px solid #30363d;border-radius:8px">
          <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">\u{1F4DD} Registrar movimiento manual</h3>
          <p style="margin:0;color:#8b949e;font-size:0.875rem">
            Salidas de stock (pérdidas, daños, devoluciones) o aumentos. Se registran via NexoAPI y aparecen en la lista a la derecha.
          </p>

          <!-- MOV-V2 punto 7: grid responsive (3 col desktop / 2 col movil). Punto 3: orden
               Fecha (fija) -> Insumo -> Tipo -> Cantidad -> Costo. Punto 5: sin Referencia. -->
          <div class="cv-mov-grid">
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Fecha <span style="color:#f85149">*</span>
              <input type="date" name="fecha" class="form-input form-input-sm" required value="${escHtml(state.movimientoForm.fecha)}"/>
            </label>

            <!-- T169 pieza B: el insumo se BUSCA en el servidor (antes un select con el catalogo entero, que topaba
                 en 2500). div y no label: un label que envuelve un buscador le roba el foco. -->
            <div class="cv-mov-full" style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              <label for="cv-mov-insumo-buscar">Insumo <span style="color:#f85149">*</span></label>
              <input type="search" id="cv-mov-insumo-buscar" data-cv-mov-insumo-buscar class="form-input form-input-sm"
                     placeholder="Escribe para buscar el insumo" autocomplete="off">
              <input type="hidden" name="insumoId" data-cv-mov-insumo>
              <div data-cv-mov-insumo-elegido style="color:#e6edf3;font-size:0.875rem"></div>
              <div data-cv-mov-insumo-lista style="max-height:220px;overflow-y:auto;border:1px solid #30363d;border-radius:6px"></div>
            </div>

            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Tipo <span style="color:#f85149">*</span>
              <select name="tipo" class="form-input form-input-sm" required data-cv-mov-tipo>
                ${tipoMovOpts(state.movBusinessType).map(o => `<option value="${o.code}" ${o.code === state.movimientoForm.tipo ? 'selected' : ''}>${o.label}</option>`).join('')}
              </select>
            </label>

            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Cantidad <span style="color:#f85149">*</span>
              <input type="number" name="cantidad" class="form-input form-input-sm" min="0.001" step="any" required value="${state.movimientoForm.cantidad}" data-cv-mov-cantidad/>
            </label>

            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Costo estimado <small style="color:#6e7681">(auto)</small>
              <input type="number" name="total" class="form-input form-input-sm" min="0" step="any" value="${state.movimientoForm.total}" data-cv-mov-total readonly title="Costo de referencia del insumo × cantidad. Se calcula solo." style="background:#0d1117;color:#8b949e"/>
            </label>

            <label class="cv-mov-full" style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Motivo <span style="color:#f85149">*</span>
              <textarea name="motivo" class="form-input form-input-sm" rows="2" required
                        placeholder="Ej: producto vencido, caja danada, cliente devuelve&hellip;">${escHtml(state.movimientoForm.motivo)}</textarea>
            </label>

            <div class="cv-mov-full" style="display:flex;gap:8px;justify-content:flex-end">
              <button type="reset" class="btn-ghost btn-sm">Limpiar</button>
              <button type="submit" class="btn-primary btn-sm">Registrar</button>
            </div>
          </div>

          <div data-cv-mov-result style="font-size:0.875rem"></div>
        </form>

        <div style="display:flex;flex-direction:column;gap:10px">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
            <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">Movimientos recientes</h3>
            <select data-cv-mov-filter class="form-input form-input-sm" style="font-size:0.875rem;width:auto">
              ${tipoFilterOpts(state.movBusinessType).map(o => `<option value="${o.code}" ${o.code === state.movimientosFilters.tipo ? 'selected' : ''}>${o.label}</option>`).join('')}
            </select>
          </div>
          <div data-cv-mov-listwrap style="border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117"></div>
        </div>
      </div>
    `;

    const $form = $body.querySelector('[data-cv-mov-form]');
    const $result = $body.querySelector('[data-cv-mov-result]');
    const $filter = $body.querySelector('[data-cv-mov-filter]');

    // MOV-V2 punto 4: costo automatico = costo_referencia del insumo × cantidad.
    function recomputeCosto() {
      const $ins = $form.querySelector('[data-cv-mov-insumo]');
      const $cant = $form.querySelector('[data-cv-mov-cantidad]');
      const $total = $form.querySelector('[data-cv-mov-total]');
      if (!$ins || !$cant || !$total) return;
      const ins = state.movimientoInsumos.find(i => String(i.insumo_id ?? i.id) === String($ins.value));
      const costoRef = Number(ins?.costo_referencia ?? 0);
      const cant = Number($cant.value) || 0;
      const total = costoRef > 0 && cant > 0 ? Math.round(costoRef * cant) : 0;
      $total.value = total;
      state.movimientoForm.total = total;
    }
    // T169 pieza B: buscar el insumo en el servidor (25 por busqueda) en vez de traer el catalogo entero.
    // Lo elegido y lo visto quedan en state.movimientoInsumos, del que sale el costo de referencia.
    const $buscar = $form.querySelector('[data-cv-mov-insumo-buscar]');
    const $lista = $form.querySelector('[data-cv-mov-insumo-lista]');
    const $elegido = $form.querySelector('[data-cv-mov-insumo-elegido]');
    const $insumoId = $form.querySelector('[data-cv-mov-insumo]');
    let resultadosVisibles = [];
    function pintarResultados(res, q) {
      if (!res?.ok) {
        $lista.innerHTML = `<div style="padding:8px 10px;color:#d29922">No pudimos buscar ahora. Intenta de nuevo.</div>`;
        return;
      }
      resultadosVisibles = sinInsumosRepetidos(res.data?.items || []);
      const total = Number(res.data?.totalCount ?? resultadosVisibles.length);
      for (const i of resultadosVisibles) {
        if (!state.movimientoInsumos.some((x) => String(x.insumo_id ?? x.id) === String(i.insumo_id ?? i.id))) state.movimientoInsumos.push(i);
      }
      if (!resultadosVisibles.length) {
        $lista.innerHTML = `<div style="padding:8px 10px">Nada con ese nombre${q ? ` (“${escHtml(q)}”)` : ''}.</div>`;
        return;
      }
      const elegido = $insumoId.value;
      $lista.innerHTML = resultadosVisibles.map((i, k) => {
        const id = String(i.insumo_id ?? i.id);
        return `<button type="button" data-cv-mov-insumo-opcion="${k}" style="display:block;width:100%;text-align:left;min-height:36px;padding:6px 10px;border:0;border-bottom:1px solid #21262d;background:${id === elegido ? '#1f6feb33' : 'transparent'};color:#e6edf3;font-size:0.875rem;cursor:pointer">${escHtml(etiquetaInsumo(i))}</button>`;
      }).join('') + (total > resultadosVisibles.length
        ? `<div data-cv-mov-insumo-mas style="padding:6px 10px">Mostrando ${resultadosVisibles.length} de ${total.toLocaleString('es-CO')}: escribe más para afinar.</div>` : '');
    }
    const buscador = crearBuscador({
      buscar: (q) => InsumosApiClient.searchInsumos(getSelectedBusiness(), { q, page: 1, pageSize: 25 }),
      pintar: pintarResultados
    });
    function elegirInsumo(i) {
      $insumoId.value = i ? String(i.insumo_id ?? i.id) : '';
      state.movimientoForm.insumoId = $insumoId.value;
      $elegido.textContent = i ? `Elegido: ${etiquetaInsumo(i)}` : '';
      recomputeCosto();
    }
    $buscar?.addEventListener('input', () => buscador.teclear($buscar.value.trim()));
    $lista?.addEventListener('click', (e) => {
      const $op = e.target.closest('[data-cv-mov-insumo-opcion]');
      if (!$op) return;
      elegirInsumo(resultadosVisibles[Number($op.dataset.cvMovInsumoOpcion)]);
      $lista.querySelectorAll('[data-cv-mov-insumo-opcion]').forEach(($b) => { $b.style.background = $b === $op ? '#1f6feb33' : 'transparent'; });
    });
    $form.querySelector('[data-cv-mov-cantidad]')?.addEventListener('input', recomputeCosto);

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const businessId = getSelectedBusiness();
      if (!businessId) {
        $result.innerHTML = `<span style="color:#d29922">Selecciona un negocio en la barra superior antes de registrar.</span>`;
        return;
      }
      const fd = new FormData($form);
      const insumoId = (fd.get('insumoId') || '').trim();
      const cantidad = Number(fd.get('cantidad'));
      // PANEL-MOVIMIENTO-FORM-INSUMO-CANTIDAD-V1-001: backend exige insumoId + cantidad.
      // Validar en el front para dar mensaje claro en vez del 400 crudo del backend.
      if (!insumoId) {
        $result.innerHTML = `<span style="color:#d29922">Selecciona el insumo afectado antes de registrar.</span>`;
        return;
      }
      if (!(cantidad > 0)) {
        $result.innerHTML = `<span style="color:#d29922">La cantidad debe ser mayor a 0.</span>`;
        return;
      }
      // MOVM-1/3 (bit #3022): usuario + hora reales — sin esto el movimiento baja a
      // Desktop sin quien lo registro ni a que hora (MOV-6 de Argy-D mapea ambos).
      const operador = getOperador();
      const now = new Date();
      const hora = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const payload = {
        tipo:        fd.get('tipo'),
        insumoId,
        cantidad,
        referencia:  fd.get('referencia') || undefined,
        fecha:       fd.get('fecha'),
        hora,
        usuario:     operador?.nombre || 'panel-manager',
        total:       Number(fd.get('total')) || 0,
        motivo:      fd.get('motivo')
      };
      $result.innerHTML = `<span style="color:#8b949e">&#x21BB; registrando&hellip;</span>`;
      const res = await ComprasApiClient.createMovimiento(businessId, payload);
      if (!res.ok) {
        $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
        return;
      }
      $result.innerHTML = `<span style="color:#3fb950">✓ Movimiento <strong>${escHtml(res.data.referencia || '(sin referencia)')}</strong> registrado.</span>`;
      $form.reset();
      Object.assign(state.movimientoForm, {
        tipo: 'desperdicio', insumoId: '', referencia: '', fecha: new Date().toISOString().slice(0, 10), cantidad: 1, total: 0, motivo: ''
      });
      // T169 pieza B: reset() no limpia un input hidden (su value ES su valor por defecto): se limpia a mano.
      $buscar.value = '';
      elegirInsumo(null); // MOV-V2: el costo vuelve a 0 tras limpiar
      buscador.ahora('');
      await loadMovimientos();
    });

    $filter.addEventListener('change', async () => {
      state.movimientosFilters.tipo = $filter.value;
      await loadMovimientos();
    });

    // T169 pieza B: al abrir se ve la primera pagina (por nombre), no el catalogo entero. Un buscador vacio que no
    // muestra nada se lee como "no hay insumos" (lo mismo que paso en el picker de Presupuestos).
    if (getSelectedBusiness()) buscador.ahora('');

    // MOV-V3: vocabulario adaptativo — fetch del business_type del negocio seleccionado
    // (async, no bloquea; mientras llega se muestra el vocabulario neutro "Pérdida").
    const bizIdForType = getSelectedBusiness();
    if (bizIdForType && state.movBizTypeFor !== bizIdForType) {
      BusinessApiClient.getById(bizIdForType).then(res => {
        if (!res.ok) return; // sin dato de tipo: se queda el vocabulario neutro (regla: Merma SOLO en CM confirmado)
        const b = res.data?.data ?? res.data ?? {};
        state.movBusinessType = b.businessType ?? b.business_type ?? '';
        state.movBizTypeFor = bizIdForType;
        // Repintar ambos selects conservando la seleccion actual.
        const $tipo = $form.querySelector('[data-cv-mov-tipo]');
        if ($tipo) {
          const cur = $tipo.value;
          $tipo.innerHTML = tipoMovOpts(state.movBusinessType).map(o => `<option value="${o.code}" ${o.code === cur ? 'selected' : ''}>${o.label}</option>`).join('');
        }
        if ($filter) {
          const curF = $filter.value;
          $filter.innerHTML = tipoFilterOpts(state.movBusinessType).map(o => `<option value="${o.code}" ${o.code === curF ? 'selected' : ''}>${o.label}</option>`).join('');
        }
        renderMovimientosList(); // badges de la lista tambien adoptan el vocabulario
      });
    }
    await loadMovimientos();
  }

  async function loadMovimientos() {
    const businessId = getSelectedBusiness();
    const $wrap = $body.querySelector('[data-cv-mov-listwrap]');
    if (!$wrap) return;
    if (!businessId) {
      $wrap.innerHTML = `<div style="padding:18px;color:#d29922;text-align:center">Selecciona un negocio</div>`;
      return;
    }
    state.movimientosLoading = true;
    $wrap.innerHTML = `<div style="padding:18px;color:#8b949e;text-align:center">&#x21BB; cargando&hellip;</div>`;
    // MV-T2 (bit #3349): filtrar por un code canonico debe traer TAMBIEN el
    // historico legacy (desperdicio->merma, aumento->ajuste). El backend filtra
    // por igualdad estricta, asi que se piden ambos codes en paralelo y se
    // mezclan ordenados por fecha+hora desc. Si una de las dos falla (ej. API
    // sin MV-T1 aun), se muestra la que respondio.
    const legacyTipo = LEGACY_TIPO_EQUIV[state.movimientosFilters.tipo];
    let res;
    if (legacyTipo) {
      const [resNew, resLegacy] = await Promise.all([
        ComprasApiClient.listMovimientos(businessId, state.movimientosFilters),
        ComprasApiClient.listMovimientos(businessId, { ...state.movimientosFilters, tipo: legacyTipo })
      ]);
      if (!resNew.ok && !resLegacy.ok) {
        res = resNew;
      } else {
        const items = [
          ...(resNew.ok ? (resNew.data?.items || []) : []),
          ...(resLegacy.ok ? (resLegacy.data?.items || []) : [])
        ].sort((a, b) => String(`${b.fecha} ${b.hora || ''}`).localeCompare(String(`${a.fecha} ${a.hora || ''}`)));
        res = { ok: true, data: { items } };
      }
    } else {
      res = await ComprasApiClient.listMovimientos(businessId, state.movimientosFilters);
    }
    state.movimientosLoading = false;
    if (!res.ok) {
      $wrap.innerHTML = `<div style="padding:18px;color:#f85149">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 100))}</div>`;
      return;
    }
    state.movimientos = res.data?.items || [];
    renderMovimientosList();
  }

  // PANEL-MOVIMIENTO-FORM-INSUMO-CANTIDAD-V1-001 M6: la referencia de un movimiento
  // ligado a compra viene como "FACTURA_COMPRA:<uuid>|ITEM:<uuid>" y se pintaba entero
  // en 3 renglones azules. La truncamos a chips cortos legibles (FACTURA e7b14648 /
  // ITEM 8747) conservando la ref completa en title.
  const _refShortId = (v) => {
    const s = String(v || '').trim();
    if (!s) return '';
    // uuid u id largo -> primeros 8 chars del primer bloque; corto -> tal cual.
    return s.split('-')[0].slice(0, 8);
  };
  const _REF_TYPE_LABEL = { FACTURA_COMPRA: 'FACTURA', FACTURA: 'FACTURA', COMPRA: 'COMPRA', ITEM: 'ITEM', MOV: 'MOV' };
  function formatMovRef(ref) {
    const raw = String(ref || '').trim();
    if (!raw) return '<span style="color:#6e7681">-</span>';
    // Ref manual corta (MER-001, sin patron TYPE:value) -> mostrar tal cual.
    if (!raw.includes(':')) return `<span style="color:#79c0ff;font-family:monospace;font-size:0.875rem">${escHtml(raw)}</span>`;
    const chips = raw.split('|').map(seg => {
      const idx = seg.indexOf(':');
      const type = seg.slice(0, idx).trim().toUpperCase();
      const val = seg.slice(idx + 1);
      const label = _REF_TYPE_LABEL[type] || type;
      return `<span style="display:inline-block;background:#1f6feb18;color:#79c0ff;border-radius:4px;padding:1px 6px;font-size:0.875rem;font-family:monospace;white-space:nowrap">${escHtml(label)} ${escHtml(_refShortId(val))}</span>`;
    });
    return chips.join(' ');
  }

  function renderMovimientosList() {
    const $wrap = $body.querySelector('[data-cv-mov-listwrap]');
    if (!$wrap) return;
    if (state.movimientos.length === 0) {
      $wrap.innerHTML = `<div style="padding:18px;color:#8b949e;text-align:center">Sin movimientos para este filtro.</div>`;
      return;
    }
    $wrap.innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
        <thead style="background:#161b22">
          <tr>
            ${['Tipo', 'Ref.', 'Fecha', 'Cant.', 'Motivo', 'Acciones'].map(h => `
              <th style="padding:6px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">${escHtml(h)}</th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
          ${state.movimientos.map(m => {
            // MV-T2: tipoMovMeta ya rotula Desperdicio/Aumento para los codes
            // canonicos Y los legacy (merma/ajuste). Aqui solo se aplica el
            // vocabulario adaptativo no-comida (Pérdida) sobre ambos codes.
            const meta = { ...ComprasApiClient.tipoMovMeta(m.tipo) };
            const tLower = String(m.tipo || '').toLowerCase();
            if ((tLower === 'desperdicio' || tLower === 'merma') && !isFoodBusiness(state.movBusinessType)) meta.label = 'Pérdida';
            return `
              <tr style="border-bottom:1px solid #21262d">
                <td style="padding:6px 10px">
                  <span style="background:${meta.color}22;color:${meta.color};padding:2px 8px;border-radius:8px;font-size:0.875rem;font-weight:600">
                    ${meta.icon} ${escHtml(meta.label)}
                  </span>
                </td>
                <td style="padding:6px 10px;max-width:170px" title="${escHtml(m.referencia || '')}">
                  <div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center">${formatMovRef(m.referencia)}</div>
                </td>
                <td style="padding:6px 10px;color:#c9d1d9">${escHtml(m.fecha)}${m.hora ? ' ' + escHtml(m.hora) : ''}</td>
                <td style="padding:6px 10px;color:#c9d1d9">${escHtml(m.cantidad)} ${escHtml(m.insumo_presentacion || '')}</td>
                <td style="padding:6px 10px;color:#c9d1d9;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${escHtml(m.insumo_nombre || m.motivo)}">${escHtml(m.insumo_nombre || m.motivo || '-')}</td>
                <td style="padding:6px 10px;text-align:right;white-space:nowrap">
                  <button type="button" data-cv-edit-mov="${escHtml(m.id)}" class="btn-ghost btn-sm" title="Editar movimiento" style="font-size:0.875rem;padding:3px 9px;margin-right:5px;border:1px solid #30363d">✏ Editar</button>
                  <button type="button" data-cv-del-mov="${escHtml(m.id)}" class="btn-sm" title="Eliminar movimiento" style="font-size:0.875rem;padding:3px 9px;color:#f85149;background:#f8514918;border:1px solid #f8514955;border-radius:5px;cursor:pointer">🗑 Eliminar</button>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    `;

    $wrap.querySelectorAll('[data-cv-edit-mov]').forEach(btn => {
      btn.addEventListener('click', () => {
        const m = state.movimientos.find(x => String(x.id) === btn.dataset.cvEditMov);
        if (m) openEditarMovimientoModal(m);
      });
    });
    $wrap.querySelectorAll('[data-cv-del-mov]').forEach(btn => {
      btn.addEventListener('click', () => {
        const m = state.movimientos.find(x => String(x.id) === btn.dataset.cvDelMov);
        if (m) confirmDeleteMovimiento(m);
      });
    });
  }

  // ============== Tab 5: Estadisticas (MV-T5, bit #3391) ==============
  // Fase 2 del canon: cuanto se desperdicia normalmente por insumo, cuanto rinde al
  // procesarse, y que movimientos se salieron de lo normal. Datos del endpoint
  // MV-T4 (contrato #3393). Null-safe: si el backend no lo tiene, el tab lo dice y
  // no rompe nada.

  const fmtQty = (n) => (n == null || isNaN(Number(n)))
    ? '—'
    : Number(n).toLocaleString('es-CO', { maximumFractionDigits: 2 });

  // Unidad del insumo: el endpoint de estadisticas no la manda, se cruza con el
  // catalogo que el tab de movimientos ya carga.
  function unidadDeInsumo(insumoId) {
    const i = state.movimientoInsumos.find(x => String(x.insumo_id ?? x.id) === String(insumoId));
    return i?.unidad ? String(i.unidad) : '';
  }

  async function renderEstadisticasTab() {
    const businessId = getSelectedBusiness();
    if (!businessId) {
      $body.innerHTML = `<div style="padding:30px;text-align:center;color:#d29922">Selecciona un negocio para ver sus estadísticas.</div>`;
      return;
    }
    $body.innerHTML = `<div style="padding:30px;text-align:center;color:#8b949e">&#x21BB; calculando promedios&hellip;</div>`;

    // El catalogo de insumos da las unidades; si falla, la tabla igual se pinta.
    if (!state.catalogoInsumosCompleto) await loadMovimientoInsumos(businessId).catch(() => {});

    const res = await ComprasApiClient.getEstadisticasMovimientos(businessId, state.estadisticasFilters);

    if (res.ok && res.disponible === false) {
      $body.innerHTML = `
        <div style="padding:24px;text-align:center;color:#8b949e;background:#161b22;border:1px solid #30363d;border-radius:8px">
          <div style="font-size:1.6rem;margin-bottom:6px">📊</div>
          <div style="color:#c9d1d9;font-weight:600;margin-bottom:4px">Estadísticas todavía no disponibles</div>
          <div style="font-size:0.875rem">Esta vista necesita una actualización del servidor que aún no está publicada.
          Los movimientos se siguen registrando con normalidad.</div>
        </div>`;
      return;
    }
    if (!res.ok) {
      $body.innerHTML = `<div style="padding:24px;color:#f85149">No pude cargar las estadísticas: ${escHtml(res.error?.message || res.error?.code || 'error desconocido')}</div>`;
      return;
    }

    state.estadisticas = res.data;
    renderEstadisticasContenido();
  }

  function renderEstadisticasContenido() {
    const d = state.estadisticas;
    const insumos = d?.insumos || [];
    const anomalias = d?.anomalias || [];
    const per = d?.periodo;
    const semanas = per?.semanas ?? state.estadisticasFilters.semanas ?? 8;

    const contexto = per
      ? `${escHtml(per.desde || '')} → ${escHtml(per.hasta || '')} · ${escHtml(String(semanas))} semanas`
      : `últimas ${escHtml(String(semanas))} semanas`;

    // Solo tienen sentido las filas con algun dato; un insumo sin desperdicio ni
    // aumento no aporta nada a esta lectura.
    const conDatos = insumos.filter(i => i.desperdicio_promedio_semanal != null || i.aumento_ratio != null);

    const filas = conDatos.map(i => {
      const u = unidadDeInsumo(i.insumo_id);
      const desp = i.desperdicio_promedio_semanal != null
        ? `${fmtQty(i.desperdicio_promedio_semanal)}${u ? ' ' + escHtml(u) : ''}
           <div style="font-size:0.875rem;color:#6e7681">en ${i.desperdicio_semanas_con_datos} de ${escHtml(String(semanas))} semanas</div>`
        : '<span style="color:#6e7681">sin desperdicio</span>';
      // Rendimiento: 1 + ratio (0.35 -> 1.35x = gana 35%). Se muestran ambas
      // lecturas porque "1.35x" y "+35%" le hablan a personas distintas.
      const rend = i.aumento_rendimiento != null
        ? `${fmtQty(i.aumento_rendimiento)}x
           <span style="color:${i.aumento_ratio >= 0 ? '#3fb950' : '#f85149'};font-size:0.875rem">
             (${i.aumento_ratio >= 0 ? '+' : ''}${fmtQty(i.aumento_ratio * 100)}%)</span>
           <div style="font-size:0.875rem;color:#6e7681">${i.aumento_muestras} muestra${i.aumento_muestras === 1 ? '' : 's'}${
             i.aumento_sin_entrada_previa > 0
               ? ` · <span style="color:#d29922" title="Esos aumentos no tenían una entrada previa con la cual comparar, así que no entraron en el cálculo">${i.aumento_sin_entrada_previa} sin comparación</span>`
               : ''}</div>`
        : '<span style="color:#6e7681">sin datos</span>';
      return `
        <tr style="border-bottom:1px solid #21262d">
          <td style="padding:7px 10px;color:#c9d1d9">${escHtml(i.insumo_nombre)}</td>
          <td style="padding:7px 10px;text-align:right;color:#c9d1d9">${desp}</td>
          <td style="padding:7px 10px;text-align:right;color:#c9d1d9">${rend}</td>
        </tr>`;
    }).join('');

    const anomaliasHtml = anomalias.length === 0
      ? `<div style="padding:14px;color:#8b949e;font-size:0.875rem;text-align:center">Nada fuera de lo normal en este periodo. 👌</div>`
      : `<table style="width:100%;border-collapse:collapse;font-size:0.875rem">
           <thead style="background:#161b22"><tr>
             ${['Insumo', 'Tipo', 'Cantidad', 'Lo normal', 'Fecha'].map(h => `
               <th style="padding:6px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">${escHtml(h)}</th>`).join('')}
           </tr></thead>
           <tbody>
             ${anomalias.map(a => {
               const meta = ComprasApiClient.tipoMovMeta(a.tipo);
               const u = unidadDeInsumo(a.insumo_id);
               return `
                 <tr style="border-bottom:1px solid #21262d">
                   <td style="padding:6px 10px;color:#c9d1d9">${escHtml(a.insumo_nombre)}</td>
                   <td style="padding:6px 10px"><span style="background:${meta.color}22;color:${meta.color};padding:2px 8px;border-radius:8px;font-size:0.875rem;font-weight:600">${meta.icon} ${escHtml(meta.label)}</span></td>
                   <td style="padding:6px 10px;color:#f85149;font-weight:600">${fmtQty(a.cantidad)}${u ? ' ' + escHtml(u) : ''}
                     <span style="color:#d29922;font-size:0.875rem">${fmtQty(a.veces)}× lo normal</span></td>
                   <td style="padding:6px 10px;color:#8b949e">${fmtQty(a.promedio_comparado)}${u ? ' ' + escHtml(u) : ''}</td>
                   <td style="padding:6px 10px;color:#c9d1d9">${escHtml(a.fecha)}</td>
                 </tr>`;
             }).join('')}
           </tbody>
         </table>`;

    $body.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px">
          <div style="color:#8b949e;font-size:0.875rem">📅 ${contexto}</div>
          <label style="display:flex;align-items:center;gap:6px;font-size:0.875rem;color:#8b949e">
            Ventana
            <select data-cv-est-semanas class="form-input form-input-sm" style="font-size:0.875rem;width:auto">
              ${[4, 8, 12, 26].map(n => `<option value="${n}" ${n === semanas ? 'selected' : ''}>${n} semanas</option>`).join('')}
            </select>
          </label>
        </div>

        <div style="border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
          <div style="padding:10px 14px;background:#161b22;font-size:0.875rem;color:#f0f6fc;border-bottom:1px solid #30363d">
            ⚠️ Fuera de lo normal (${anomalias.length})
            <div style="font-size:0.875rem;color:#8b949e;margin-top:2px">Movimientos que superan ${fmtQty(d?.factor_anomalia)}× lo habitual de ese insumo. Se comparan contra los <em>otros</em> movimientos del mismo tipo, y hacen falta al menos dos para poder comparar.</div>
          </div>
          ${anomaliasHtml}
        </div>

        <div style="border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
          <div style="padding:10px 14px;background:#161b22;font-size:0.875rem;color:#f0f6fc;border-bottom:1px solid #30363d">
            📊 Por insumo (${conDatos.length})
          </div>
          ${conDatos.length === 0
            ? `<div style="padding:18px;text-align:center;color:#8b949e;font-size:0.875rem">Todavía no hay desperdicios ni aumentos registrados en este periodo.</div>`
            : `<table style="width:100%;border-collapse:collapse;font-size:0.875rem">
                 <thead style="background:#161b22"><tr>
                   <th style="padding:6px 10px;text-align:left;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">Insumo</th>
                   <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d" title="Promedio por semana, contando solo las semanas en que hubo desperdicio">Desperdicio semanal</th>
                   <th style="padding:6px 10px;text-align:right;color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d" title="Cuánto queda tras procesar: 1.35x significa que gana 35% de peso">Rendimiento al procesar</th>
                 </tr></thead>
                 <tbody>${filas}</tbody>
               </table>`}
        </div>
      </div>`;

    $body.querySelector('[data-cv-est-semanas]')?.addEventListener('change', async (e) => {
      state.estadisticasFilters.semanas = Number(e.target.value) || 8;
      await renderEstadisticasTab();
    });
  }

  // ============== Tab 4: Aprobaciones (ActionArea + match-review real) ==============
  // PANEL-COMPRAS-OCR-MATCH-IN-ACTION-AREA-001:
  // Subir factura OCR -> workflow Purchase V1.0 procesa imagen -> response trae tabla[]
  // de items_pendientes con candidatos de matcheo -> action-area abre match-review.

  function renderAprobacionesTab() {
    $body.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="padding:14px 16px;background:#161b22;border:1px solid #30363d;border-radius:8px">
          <h3 style="margin:0 0 6px;font-size:0.95rem;color:#f0f6fc">✨ Aprobaciones de matcheo</h3>
          <p style="margin:0;color:#c9d1d9;font-size:0.875rem;line-height:1.5">
            Sube una factura para que el OCR la procese. Si quedan items sin matchear con tu inventario,
            aparecera una <strong>tabla interactiva</strong> donde puedes elegir el insumo correcto por cada item
            (o pedir crear uno nuevo). Confirmas la aprobacion y se aplica al inventario en un solo paso.
          </p>
        </div>

        <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center">
          <button type="button" data-cv-upload-factura class="btn-primary btn-sm">
            \u{1F4E4} Subir factura(s) OCR
          </button>
          <div style="flex:1"></div>
          <button type="button" data-cv-demo-clear class="btn-ghost btn-sm"
                  title="Cerrar action-area" aria-label="Cerrar action-area">✕ Cerrar area</button>
        </div>

        <div data-cv-actionarea></div>

        <div style="padding:10px 12px;background:#3fb95022;border:1px dashed #3fb950;border-radius:6px;color:#56d364;font-size:0.875rem;line-height:1.4">
          &#10003; Real: el upload OCR ya invoca el workflow <code>Purchase V1.0</code> (n8n) y la tabla
          interactiva sale del LLM. Al confirmar, POST a <code>/webhook/purchase/match</code> aplica los
          matches al inventario.
        </div>
      </div>
    `;

    const $aaContainer = $body.querySelector('[data-cv-actionarea]');
    if (!state.actionArea) {
      state.actionArea = createActionArea($aaContainer);
    } else {
      // Si la accion ya existia (volvimos a la pestana), recreamos sobre el nuevo container
      state.actionArea = createActionArea($aaContainer);
    }

    $body.querySelector('[data-cv-demo-clear]').addEventListener('click', () => {
      if (state.actionArea) state.actionArea.clear();
    });

    // ===== Subir factura(s) OCR -> webhook Purchase n8n =====
    $body.querySelector('[data-cv-upload-factura]').addEventListener('click', () => {
      state.actionArea.showMode('inline-upload', {
        label: 'Sube factura(s) - OCR Gemini procesa cada imagen y crea factura+items+egreso+movimiento',
        accept: 'image/png,image/jpeg,image/webp,application/pdf',
        maxSize: 8 * 1024 * 1024, // 8MB
        multiple: true,
        onSend: async (files) => {
          const businessId = getSelectedBusiness();
          if (!businessId) {
            avisar('No hay negocio seleccionado. Selecciona uno en el encabezado primero.', { tono: 'error' });
            return;
          }

          const $aaContainer = $body.querySelector('[data-cv-actionarea]');
          const log = (html) => {
            const $log = $aaContainer.querySelector('[data-cv-upload-log]');
            if ($log) $log.innerHTML += html;
          };

          // T13 (bit #952): mensajes UX no-tecnicos. Sin "OCR Purchase" ni jerga.
          const tituloHeader = files.length === 1 ? '📤 Procesando factura…' : `📤 Procesando ${files.length} facturas…`;
          $aaContainer.innerHTML = `
            <div style="padding:14px;background:#0d1117;border:1px solid #30363d;border-radius:8px">
              <h4 style="margin:0 0 8px;color:#f0f6fc;font-size:0.95rem">${tituloHeader}</h4>
              <div data-cv-upload-log style="font-size:0.875rem;color:#c9d1d9;line-height:1.8"></div>
              <button type="button" data-cv-upload-close class="btn-ghost btn-sm" style="margin-top:10px">Cerrar</button>
            </div>
          `;
          $aaContainer.querySelector('[data-cv-upload-close]').addEventListener('click', () => {
            if (state.actionArea) state.actionArea.clear();
          });

          // PANEL-COMPRAS-OCR-MATCH-IN-ACTION-AREA-001:
          // Recolectar response de cada upload — la primera con tabla[] dispara match-review.
          let firstMatchPayload = null;
          // PANEL-POST-OCR-MATCH-REDIRECT-V1-001 (bit #971):
          // Caso defensivo: workflow puede crear factura con items match_status=PENDING SIN
          // emitir tabla[] (ej. CH 196480: LLM no encontro candidatos para "papa"). La sennal
          // viene via resumen.items_total > match_sugeridos + crear_sugeridos + review.
          // En ese caso NO mostrar "Factura registrada" y redirigir al user al listado con
          // filtro match=PENDING para que resuelva via Tab Detalle.
          let firstPendingFactura = null;
          // OCR-FACT-REVIEW: id de la primera factura del camino feliz (todo auto-match).
          // Antes se descartaba y el usuario nunca veia un item — el reporte de Josse.
          let firstRegistradaId = null;

          for (let i = 0; i < files.length; i++) {
            const f = files[i];
            // T13 (bit #952): "Subiendo factura..." sin nombre de archivo ni KB.
            const prefijo = files.length > 1 ? `[${i + 1}/${files.length}] ` : '';
            log(`<div>📄 ${prefijo}Subiendo factura…</div>`);
            try {
              // KIOSK-ARGEMIRO-COMPRA-V1 (T1): webhook + parse Gemini extraidos a
              // ComprasApiClient.uploadOcr (URL unica reutilizable por el kiosk).
              const up = await ComprasApiClient.uploadOcr(businessId, f, { source: 'panel-manager' });

              if (up.ok) {
                const payload = up.payload || {};

                const tabla = Array.isArray(payload?.tabla) ? payload.tabla : [];
                const resumen = payload?.resumen || {};
                const factura_id = payload?.factura_id || null;
                const pregunta = payload?.pregunta_al_usuario || null;

                // PANEL-POST-OCR-MATCH-REDIRECT-V1-001 (bit #971):
                // Detectar PENDING por dos vias:
                //   PRIMARIO   : tabla[] con items para revisar (caso normal — workflow OK).
                //   DEFENSIVO  : tabla=[] PERO factura_id presente y resumen.items_total
                //                indica items que no fueron auto-matcheados por el LLM
                //                (caso CH 196480: papa sin candidatos en catalogo).
                const itemsTotal = Number(resumen.items_total) || 0;
                const matchSug = Number(resumen.match_sugeridos) || 0;
                const crearSug = Number(resumen.crear_sugeridos) || 0;
                const reviewSug = Number(resumen.review) || 0;
                const autoMatcheados = matchSug + crearSug;
                const hasTablaItems = tabla.length > 0;
                const possiblePendingSinTabla = !hasTablaItems
                  && !!factura_id
                  && (
                    (itemsTotal > 0 && (autoMatcheados + reviewSug) < itemsTotal)
                    || reviewSug > 0
                  );

                if (hasTablaItems) {
                  // T13 (bit #952): mensaje natural en lugar de "OK 200 en Xms"
                  const itemsTxt = tabla.length === 1 ? '1 item por revisar' : `${tabla.length} items por revisar`;
                  log(`<div style="color:#3fb950">✅ ${prefijo}Factura procesada — ${itemsTxt}</div>`);
                  if (!firstMatchPayload) {
                    // Workflow Purchase v33+ devuelve factura_total + factura_codigo + factura_id
                    // top-level snake_case (via prompt Gemini que copia desde payload_agente).
                    // Fallback legacy: payload.factura.Total/Codigo del shape original Gemini OCR.
                    const facturaInfo = payload?.factura || {};
                    firstMatchPayload = {
                      businessId,
                      facturaId: payload?.factura_id ?? factura_id,
                      tabla,
                      resumen,
                      pregunta_al_usuario: pregunta,
                      facturaTotal: payload?.factura_total ?? facturaInfo?.Total ?? facturaInfo?.total ?? null,
                      facturaCodigo: payload?.factura_codigo ?? facturaInfo?.Codigo ?? facturaInfo?.codigo ?? null
                    };
                  }
                } else if (possiblePendingSinTabla) {
                  // PANEL-POST-OCR-MATCH-REDIRECT-V1-001: factura creada pero items sin
                  // auto-match ni candidatos. Reportar al user en color advertencia y
                  // marcar firstPendingFactura para redirect al listado con filtro PENDING.
                  const pendientesEstimados = itemsTotal > 0
                    ? Math.max(itemsTotal - autoMatcheados - reviewSug, 1)
                    : (reviewSug > 0 ? reviewSug : 1);
                  const itemsTxt = pendientesEstimados === 1
                    ? '1 item sin auto-match'
                    : `${pendientesEstimados} items sin auto-match`;
                  log(`<div style="color:#d29922">⚠ ${prefijo}Factura procesada — ${itemsTxt} (revisar manualmente).</div>`);
                  if (!firstPendingFactura) {
                    firstPendingFactura = {
                      facturaId: factura_id,
                      pendientesEstimados,
                      facturaCodigo: payload?.factura_codigo ?? null
                    };
                  }
                } else {
                  // T13 (bit #952): factura sin items pendientes => texto natural sin metricas.
                  log(`<div style="color:#3fb950">✅ ${prefijo}Factura procesada</div>`);
                  if (!firstRegistradaId && factura_id) firstRegistradaId = factura_id;
                }
              } else if (up.status === 0) {
                // Fallo de red / excepcion antes de tener respuesta HTTP.
                console.error('[ComprasView] webhook purchase network error', up.error);
                log(`<div style="color:#f85149">❌ ${prefijo}Sin conexión. Verifica internet y reintenta.</div>`);
              } else {
                // T13: error sin HTTP code visible al usuario (queda en consola).
                console.error('[ComprasView] webhook purchase error', up.status, up.error);
                log(`<div style="color:#f85149">❌ ${prefijo}No pudimos procesar esta factura. Reintenta más tarde.</div>`);
              }
            } catch (err) {
              console.error('[ComprasView] purchase render error', err);
              log(`<div style="color:#f85149">❌ ${prefijo}No pudimos procesar esta factura. Reintenta más tarde.</div>`);
            }
          }

          if (firstMatchPayload) {
            // T13 (bit #952): "Listo, abriendo revision..." en lugar de "Procesamiento completo".
            log(`<div style="margin-top:8px;color:#79c0ff;font-weight:600">✓ Listo — abriendo revisión…</div>`);
            // Esperar 800ms para que el usuario vea el log antes de cambiar al match-review
            await new Promise(r => setTimeout(r, 800));
            const fidRevision = firstMatchPayload.facturaId;
            // Un solo GET del detalle para los items (cantidad/costo) y el proveedor (rubro).
            const detalleRevision = fidRevision ? ComprasApiClient.getFacturaById(businessId, fidRevision) : null;
            state.actionArea.showMode('match-review', {
              ...firstMatchPayload,
              // FV-T5 punto 1: el rubro del proveedor. Si la API dice que ya lo tiene, se muestra y no se pregunta;
              // si no, se elige y se guarda en el proveedor (las proximas facturas lo heredan).
              rubro: fidRevision ? {
                cargar: async () => {
                  const r = await detalleRevision;
                  const proveedorId = r.ok ? r.data?.proveedor_id : null;
                  if (!proveedorId) return null;
                  const rr = await FinanzasApiClient.listRubrosEgreso(businessId);
                  const opciones = rr.ok ? rr.items : [];
                  return { proveedorId, opciones, ...rubroDeRevision(r.data, getRubroCache(businessId)[proveedorId], opciones) };
                },
                guardar: async (rubroId) => {
                  const r = await detalleRevision;
                  const proveedorId = r.ok ? r.data?.proveedor_id : null;
                  if (!proveedorId) return { ok: false, error: 'sin proveedor' };
                  const res = await FinanzasApiClient.setProveedorRubro(businessId, proveedorId, rubroId);
                  if (!res.ok) return { ok: false, error: res.error?.message || res.error?.code || 'no se pudo guardar' };
                  setRubroCache(businessId, proveedorId, rubroId);
                  return { ok: true };
                }
              } : undefined,
              // FV-T5 (pedido de Josse 30-jul): cantidad y costo unitario por item, con el subtotal fijo, ANTES del
              // match. Se guardan con PATCH del item (cantidad_factura + recalc; sin match_status) y despues va el match.
              ajustes: fidRevision ? {
                cargar: async () => {
                  const r = await detalleRevision;
                  return r.ok ? (r.data?.items || []) : [];
                },
                guardar: async (cambios) => {
                  for (const c of cambios) {
                    const r = await ComprasApiClient.updateItem(businessId, fidRevision, c.id, { cantidad_factura: c.cantidad_factura, factor_empaque: c.factor_empaque, recalc: true });
                    if (!r.ok) return { ok: false, error: r.error?.message || r.error?.code || 'no se pudo guardar un item' };
                  }
                  return { ok: true };
                }
              } : undefined,
              onConfirmed: ({ ok, response, aprobaciones }) => {
                console.log('[ComprasView] match-review confirmed:', { ok, aprobaciones, response });
                // Refrescar listado facturas para reflejar cambio match_status
                if (state.activeTab === 'facturas' || state.activeTab === 'detalle') {
                  loadFacturas();
                }
              }
            });
          } else if (firstPendingFactura) {
            // PANEL-POST-OCR-MATCH-REDIRECT-V1-001 (bit #971): factura procesada pero items
            // quedaron PENDING sin candidatos -> NO mostrar "Factura registrada" (engañoso).
            // Mensaje claro + redirect a Tab Facturas con filtro match=PENDING para que el
            // user vea su factura recien subida lista para resolver desde Tab Detalle.
            const codigoStr = firstPendingFactura.facturaCodigo
              ? ` (${firstPendingFactura.facturaCodigo})`
              : '';
            const pend = firstPendingFactura.pendientesEstimados;
            const pendTxt = pend === 1 ? '1 match' : `${pend} matches`;
            // OCR-FACT-REVIEW (corte 3 del mapa): la lista filtrada dejaba la factura a
            // 3 clics. Ahora va DIRECTO al detalle de la recien subida; el filtro PENDING
            // queda puesto por si el usuario vuelve a la lista.
            state.facturasFilters = { ...state.facturasFilters, match: 'PENDING' };
            if (firstPendingFactura.facturaId) {
              log(`<div style="margin-top:8px;color:#d29922;font-weight:600">⚠ Faltan ${pendTxt} por confirmar${codigoStr} — abriendo la factura…</div>`);
              await new Promise(r => setTimeout(r, 1200));
              state.selectedFacturaId = firstPendingFactura.facturaId;
              state.selectedFacturaDetail = null;
              state.activeTab = 'detalle';
            } else {
              log(`<div style="margin-top:8px;color:#d29922;font-weight:600">⚠ Faltan ${pendTxt} por confirmar${codigoStr} — abriendo lista de pendientes…</div>`);
              await new Promise(r => setTimeout(r, 1200));
              state.activeTab = 'facturas';
            }
            renderActiveTab();
          } else if (firstRegistradaId) {
            // OCR-FACT-REVIEW (corte 1 del mapa): el camino feliz terminaba en un div
            // terminal y el usuario NUNCA veia items ni precios ("aun no se visualiza la
            // informacion de la factura" — Josse 5-ago). Ahora abre el detalle completo,
            // que ya tiene edicion por item y el banner de confirmacion.
            log(`<div style="margin-top:8px;color:#3fb950;font-weight:600">✓ Factura registrada — abriendo el detalle para que la revises…</div>`);
            await new Promise(r => setTimeout(r, 800));
            state.selectedFacturaId = firstRegistradaId;
            state.selectedFacturaDetail = null;
            state.activeTab = 'detalle';
            renderActiveTab();
          } else {
            // T13 (bit #952): "Factura registrada" cuando todos los items quedaron auto-matched.
            log(`<div style="margin-top:8px;color:#3fb950;font-weight:600">✓ Factura registrada</div>`);
          }
        }
      });
    });
  }

  // ============== Modales de edicion (T2P+T3P+T4P) ==============

  function createModalOverlay(titleHtml, formHtml) {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed; inset:0; background:rgba(0,0,0,0.7); z-index:1100; display:flex; align-items:center; justify-content:center; padding:24px; backdrop-filter:blur(4px);';
    overlay.innerHTML = `
      <div data-cv-modal style="background:#0d1117; border:1px solid #30363d; border-radius:10px; width:min(560px,95vw); max-height:90vh; overflow-y:auto; box-shadow:0 12px 48px rgba(0,0,0,0.6)">
        <div style="display:flex;justify-content:space-between;align-items:center;padding:14px 18px;border-bottom:1px solid #30363d">
          <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">${titleHtml}</h3>
          <button type="button" data-cv-modal-close class="btn-ghost btn-sm" style="font-size:0.875rem;padding:2px 10px">✕</button>
        </div>
        <div style="padding:18px">
          ${formHtml}
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    overlay.querySelector('[data-cv-modal-close]').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    return { overlay, close };
  }

  function openEditarFacturaModal(f) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const formHtml = `
      <form data-cv-edit-fac-form style="display:flex;flex-direction:column;gap:12px">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Codigo factura
          <input name="numero" type="text" class="form-input form-input-sm" value="${escHtml(f.numero || '')}">
        </label>
        <div style="display:grid;grid-template-columns:2fr 1fr;gap:10px">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Fecha
            <input name="fecha" type="date" class="form-input form-input-sm" value="${escHtml(f.fecha || '')}">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Hora
            <input name="hora" type="time" class="form-input form-input-sm" value="${escHtml((f.hora || '').slice(0,5))}">
          </label>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Valor base
            <input name="valor_base" type="text" inputmode="decimal" class="form-input form-input-sm" value="${f.valor_base || 0}">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            IVA
            <input name="iva" type="text" inputmode="decimal" class="form-input form-input-sm" value="${f.iva || 0}">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Total
            <input name="total" type="text" inputmode="decimal" class="form-input form-input-sm" value="${f.total || 0}">
          </label>
        </div>
        <div data-cv-fac-result style="font-size:0.875rem;min-height:18px"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
          <button type="button" data-cv-fac-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="submit" class="btn-primary btn-sm">Guardar cambios</button>
        </div>
      </form>
    `;
    const { overlay, close } = createModalOverlay('✏ Editar header factura', formHtml);
    const $form = overlay.querySelector('[data-cv-edit-fac-form]');
    const $result = overlay.querySelector('[data-cv-fac-result]');
    overlay.querySelector('[data-cv-fac-cancel]').addEventListener('click', close);

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData($form);
      const patch = {};
      const newNumero = (fd.get('numero') || '').trim();
      if (newNumero !== (f.numero || '')) patch.numero = newNumero;
      const newFecha = fd.get('fecha');
      if (newFecha && newFecha !== (f.fecha || '')) patch.fecha = newFecha;
      const newHora = fd.get('hora');
      if (newHora && newHora !== (f.hora || '').slice(0,5)) patch.hora = newHora;
      // OCR-T1 (bit #5260): parser colombiano + fail-loud (igual que el modal
      // de item — jamas un 0 fantasma por Number("") en tablet es-CO).
      const camposFac = [
        { name: 'valor_base', label: 'Valor base', actual: f.valor_base || 0, key: 'valor_base' },
        { name: 'iva', label: 'IVA', actual: f.iva || 0, key: 'iva' },
        { name: 'total', label: 'Total', actual: f.total || 0, key: 'total' }
      ];
      for (const c of camposFac) {
        const v = parsearNumeroColombiano(fd.get(c.name));
        if (v !== null && isNaN(v)) {
          $result.innerHTML = `<span style="color:#f85149">${escHtml(c.label)}: "${escHtml(String(fd.get(c.name)))}" no es un numero valido. Usa formato 1.234,56 o 1234.56.</span>`;
          return;
        }
        if (v !== null && v < 0) {
          $result.innerHTML = `<span style="color:#f85149">${escHtml(c.label)} no puede ser negativo.</span>`;
          return;
        }
        if (v !== null && v !== c.actual) patch[c.key] = v;
      }

      if (Object.keys(patch).length === 0) {
        $result.innerHTML = `<span style="color:#d29922">No hay cambios.</span>`;
        return;
      }

      $result.innerHTML = `<span style="color:#8b949e">↻ Guardando&hellip;</span>`;
      const res = await ComprasApiClient.updateFactura(businessId, f.id, patch);
      if (!res.ok) {
        $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
        return;
      }
      $result.innerHTML = `<span style="color:#3fb950">✓ Guardado.</span>`;
      state.selectedFacturaDetail = null;
      setTimeout(() => { close(); renderActiveTab(); }, 500);
    });
  }

  function openEditarItemModal(it, facturaId) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const candidatos = it.candidatos || [];
    const candidatosHtml = candidatos.length > 0
      ? `<div style="margin-top:6px">
           <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">Candidatos OCR</div>
           <div style="display:flex;flex-direction:column;gap:4px;max-height:120px;overflow-y:auto">
             ${candidatos.map(c => `
               <button type="button" data-cv-pick-cand="${escHtml(c.producto_existente_id)}" data-cv-pick-cand-name="${escHtml(c.nombre)}"
                       style="text-align:left;background:#161b22;border:1px solid #30363d;color:#79c0ff;padding:6px 10px;border-radius:4px;font-size:0.875rem;cursor:pointer;font-family:monospace">
                 ${escHtml(c.nombre)} <span style="color:#8b949e">${Math.round((c.similitud||0)*100)}%</span>
               </button>
             `).join('')}
           </div>
         </div>`
      : `<div style="margin-top:6px;color:#8b949e;font-size:0.875rem;font-style:italic">Sin candidatos OCR. Buscar manualmente abajo.</div>`;
    // F2-P: searchable dropdown insumos catalogo (debounce 300ms, max 25 results).
    const buscadorHtml = `
      <div style="margin-top:10px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">Buscar en catalogo</div>
        <input data-cv-insumo-search type="search" placeholder="Escribe para buscar... (min 2 chars)"
               class="form-input form-input-sm" style="font-size:0.875rem"/>
        <div data-cv-insumo-results
             style="margin-top:4px;display:none;max-height:160px;overflow-y:auto;border:1px solid #30363d;border-radius:4px;background:#161b22"></div>
      </div>
    `;

    const matchOpts = ['PENDING', 'MATCH', 'CREATE', 'REVIEW'];
    const formHtml = `
      <form data-cv-edit-item-form style="display:flex;flex-direction:column;gap:12px">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Descripcion (raw OCR)
          <input name="descripcion_raw" type="text" class="form-input form-input-sm" value="${escHtml(it.descripcion_raw || it.producto || '')}">
        </label>
        <!-- FV-T5 (bit #3355): empaque. Solo aparece si el OCR trajo los datos
             (null-safe) o si el usuario abre el detalle para corregirlos. -->
        <details ${(Number(it.cantidad_factura) > 0 || (Number(it.factor_empaque) > 0 && Number(it.factor_empaque) !== 1)) ? 'open' : ''}
                 style="background:#161b22;border:1px solid #30363d;border-radius:6px;padding:8px 10px">
          <summary style="cursor:pointer;font-size:0.875rem;color:#79c0ff;outline:none;user-select:none">📦 Empaque de la factura</summary>
          <!-- ADMIN-UX-T1: inputs proporcionales al contenido (valores de 1-4
               digitos no necesitan media pantalla — feedback captura Josse). -->
          <div style="display:flex;gap:14px;margin-top:8px;flex-wrap:wrap">
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Cantidad en la factura
              <input name="cantidad_factura" data-cv-item-cantfact type="text" inputmode="decimal" class="form-input form-input-sm" value="${it.cantidad_factura ?? ''}" placeholder="ej: 5" style="max-width:130px;text-align:center">
            </label>
            <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
              Factor de conversion
              <input name="factor_empaque" data-cv-item-factor type="text" inputmode="decimal" class="form-input form-input-sm" value="${it.factor_empaque ?? ''}" placeholder="ej: 8" style="max-width:130px;text-align:center">
            </label>
          </div>
          <div style="font-size:0.875rem;color:#8b949e;margin-top:6px;font-style:italic">
            Cantidad en la factura &times; factor = cantidad real que entra al inventario
            (unidades por empaque, o conversion de unidad como KG &rarr; gramos).
          </div>
        </details>
        <!-- ADMIN-UX-T1: de 1fr 1fr 1fr (inputs de media pantalla para 1-2
             digitos) a anchos proporcionales; numeros centrados y compactos. -->
        <div style="display:flex;gap:14px;flex-wrap:wrap">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Cantidad <small style="color:#6e7681">(unidades)</small>
            <input name="cantidad" data-cv-item-cantidad type="text" inputmode="decimal" class="form-input form-input-sm" value="${it.cantidad || 0}" style="max-width:110px;text-align:center">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Precio unit <small style="color:#6e7681">(auto)</small>
            <input name="precio_unit" data-cv-item-precio type="text" inputmode="decimal" class="form-input form-input-sm" value="${it.precio_unit || 0}" style="max-width:140px;text-align:right">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Subtotal <small style="color:#6e7681">(ancla)</small>
            <input name="subtotal" data-cv-item-subtotal type="text" inputmode="decimal" class="form-input form-input-sm" value="${it.subtotal || 0}" style="max-width:140px;text-align:right">
          </label>
        </div>
        <div data-cv-item-recalc-hint style="font-size:0.875rem;color:#8b949e;min-height:16px"></div>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Match status
          <select name="match_status" class="form-input form-input-sm">
            ${matchOpts.map(s => `<option value="${s}" ${s === it.match_status ? 'selected' : ''}>${s}</option>`).join('')}
          </select>
        </label>
        <!-- ADMIN-UX-T1 (bit #5573): esto era un <label> que envolvia buscador +
             candidatos + input UUID. Por spec HTML, click en cualquier parte de un
             label ENFOCA su primer control asociado (el input UUID del details):
             cada click en "Buscar en catalogo" mandaba el foco a otro input — el
             "se sale del buscador" que reporto Josse. Un div no roba foco. -->
        <div style="display:flex;flex-direction:column;gap:6px;font-size:0.875rem;color:#8b949e">
          <span>Insumo asignado <span style="color:#d29922">— requerido si match_status = MATCH</span></span>
          <div data-cv-insumo-name-card style="padding:8px 10px;background:#161b22;border:1px solid ${it.insumo_id ? '#3fb950' : '#30363d'};border-radius:4px;font-size:0.875rem;display:flex;flex-direction:column;gap:4px">
            <div data-cv-insumo-name-display style="font-weight:600;color:${it.insumo_id ? '#3fb950' : '#8b949e'}">${it.insumo_id ? '✓ ' + formatNullable(it.insumo_nombre || 'asignado') : '(sin asignar)'}</div>
            <div data-cv-insumo-id-chip style="font-family:monospace;font-size:0.875rem;color:#8b949e;letter-spacing:0.3px">${it.insumo_id ? escHtml(it.insumo_id) : 'pega un UUID o usa los botones abajo'}</div>
          </div>
          <details style="margin-top:2px">
            <summary style="cursor:pointer;font-size:0.875rem;color:#79c0ff;outline:none;user-select:none">⚙ Editar UUID manualmente</summary>
            <input data-cv-insumo-input name="insumo_id" type="text" placeholder="uuid o vacio" class="form-input form-input-sm" value="${escHtml(it.insumo_id || '')}" style="font-family:monospace;font-size:0.875rem;margin-top:4px">
          </details>
          ${candidatosHtml}
          ${buscadorHtml}
        </div>
        <div data-cv-item-result style="font-size:0.875rem;min-height:18px"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
          <button type="button" data-cv-item-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="submit" class="btn-primary btn-sm">Guardar cambios</button>
        </div>
      </form>
    `;
    const { overlay, close } = createModalOverlay(`✏ Editar item: ${escHtml(it.producto || '(sin nombre)')}`, formHtml);
    const $form = overlay.querySelector('[data-cv-edit-item-form]');
    const $result = overlay.querySelector('[data-cv-item-result]');
    const $insumoInput = overlay.querySelector('[data-cv-insumo-input]');

    overlay.querySelector('[data-cv-item-cancel]').addEventListener('click', close);

    // ---- FV-T5 (bit #3355): recalculo en vivo con SUBTOTAL ANCLADO ----
    // Regla del diseno (Josse 21-jul): el subtotal es el dato duro del papel, no
    // se toca al corregir cantidades. Si el colaborador corrige la cantidad (o el
    // empaque), lo que cambia es el costo unitario: precio_unit = subtotal / cantidad.
    // Se refleja al instante para que nadie apruebe a ciegas; el backend (FV-T2)
    // repite el calculo del lado servidor cuando recibe recalc=true.
    const $cantFact = overlay.querySelector('[data-cv-item-cantfact]');
    const $factor   = overlay.querySelector('[data-cv-item-factor]');
    const $cantidad = overlay.querySelector('[data-cv-item-cantidad]');
    const $precio   = overlay.querySelector('[data-cv-item-precio]');
    const $subtotal = overlay.querySelector('[data-cv-item-subtotal]');
    const $recalcHint = overlay.querySelector('[data-cv-item-recalc-hint]');
    let recalcTocado = false; // solo mandamos recalc=true si el usuario movio algo

    // OCR-T1 (bit #5260): parser colombiano UNICO — el mismo que usa el submit,
    // para que lo que el recalculo muestra sea exactamente lo que se guarda.
    const num = (el) => {
      const v = parsearNumeroColombiano(el?.value);
      return (v === null || isNaN(v)) ? null : v;
    };
    const recalcPrecio = (motivo) => {
      const cant = num($cantidad);
      const sub = num($subtotal);
      if (!(cant > 0) || !(sub > 0)) { $recalcHint.textContent = ''; return; }
      const nuevo = Math.round((sub / cant) * 100) / 100;
      $precio.value = nuevo;
      // forzarCentavos: el unitario DERIVADO es donde nace el descuadre del
      // redondeo — se muestra siempre con sus 2 decimales (criterio #5260).
      $recalcHint.innerHTML = `<span style="color:#3fb950">↻ ${escHtml(motivo)}: costo unitario recalculado a $${formatearCOP(nuevo, { forzarCentavos: true })}</span>
        <span style="color:#6e7681"> (subtotal $${formatearCOP(sub)} anclado &divide; ${escHtml(String(cant))})</span>`;
    };
    // Cantidad en factura x factor -> cantidad real de unidades -> recalcula costo.
    const recalcDesdeEmpaque = () => {
      const cf = num($cantFact);
      const fx = num($factor);
      if (!(cf > 0) || !(fx > 0)) return;
      recalcTocado = true;
      $cantidad.value = Math.round(cf * fx * 1000) / 1000;
      recalcPrecio(`${cf} × factor ${fx}`);
    };
    $cantFact?.addEventListener('input', recalcDesdeEmpaque);
    $factor?.addEventListener('input', recalcDesdeEmpaque);
    $cantidad?.addEventListener('input', () => { recalcTocado = true; recalcPrecio('Cantidad corregida'); });
    // Editar el subtotal a mano tambien re-deriva el unitario (la cantidad manda).
    $subtotal?.addEventListener('input', () => { recalcTocado = true; recalcPrecio('Subtotal corregido'); });
    // Si el usuario escribe el precio a mano, respeta su valor y deja de recalcular
    // hasta que vuelva a mover cantidad/subtotal/empaque.
    $precio?.addEventListener('input', () => { $recalcHint.innerHTML = `<span style="color:#d29922">Costo unitario editado a mano: no se recalcula.</span>`; });

    // B6-P: jerarquia nombre prominente + UUID chip secundario.
    const $insumoCard = overlay.querySelector('[data-cv-insumo-name-card]');
    const $insumoNameDisplay = overlay.querySelector('[data-cv-insumo-name-display]');
    const $insumoIdChip = overlay.querySelector('[data-cv-insumo-id-chip]');
    const setInsumoSelection = (uuid, nombre) => {
      $insumoInput.value = uuid || '';
      if (uuid) {
        $insumoNameDisplay.textContent = '✓ ' + (nombre || 'asignado');
        $insumoNameDisplay.style.color = '#3fb950';
        $insumoIdChip.textContent = uuid;
        $insumoCard.style.borderColor = '#3fb950';
      } else {
        $insumoNameDisplay.textContent = '(sin asignar)';
        $insumoNameDisplay.style.color = '#8b949e';
        $insumoIdChip.textContent = 'pega un UUID o usa los botones abajo';
        $insumoCard.style.borderColor = '#30363d';
      }
    };

    overlay.querySelectorAll('[data-cv-pick-cand]').forEach(btn => {
      btn.addEventListener('click', () => {
        setInsumoSelection(btn.dataset.cvPickCand, btn.dataset.cvPickCandName);
      });
    });

    // F2-P: searchable dropdown insumos catalogo (debounce 300ms).
    const $search = overlay.querySelector('[data-cv-insumo-search]');
    const $results = overlay.querySelector('[data-cv-insumo-results]');
    let searchTimer = null;
    let searchSeq = 0;

    const renderSearchResults = (items) => {
      if (items.length === 0) {
        $results.innerHTML = `<div style="padding:8px 12px;color:#8b949e;font-size:0.875rem;font-style:italic">Sin resultados.</div>`;
        $results.style.display = 'block';
        return;
      }
      $results.innerHTML = items.map(it => `
        <button type="button" data-cv-pick-search="${escHtml(it.insumo_id)}" data-cv-pick-search-name="${escHtml(it.nombre)}"
                style="display:block;width:100%;text-align:left;background:transparent;border:none;border-bottom:1px solid #30363d;color:#79c0ff;padding:6px 10px;font-size:0.875rem;cursor:pointer;font-family:monospace">
          <span style="color:#c9d1d9">${escHtml(it.nombre)}</span>
          ${it.presentacion ? `<span style="color:#8b949e;margin-left:6px">${escHtml(it.presentacion)}</span>` : ''}
          ${it.unidad ? `<span style="color:#8b949e;margin-left:4px">/${escHtml(it.unidad)}</span>` : ''}
          ${it.grupo ? `<span style="color:#a371f7;margin-left:6px;font-size:0.875rem">${escHtml(it.grupo)}</span>` : ''}
        </button>
      `).join('');
      $results.style.display = 'block';
      $results.querySelectorAll('[data-cv-pick-search]').forEach(btn => {
        btn.addEventListener('click', () => {
          setInsumoSelection(btn.dataset.cvPickSearch, btn.dataset.cvPickSearchName);
          $search.value = '';
          $results.style.display = 'none';
        });
      });
    };

    $search.addEventListener('input', (e) => {
      const q = e.target.value.trim();
      if (searchTimer) clearTimeout(searchTimer);
      if (q.length < 2) {
        $results.style.display = 'none';
        return;
      }
      const mySeq = ++searchSeq;
      searchTimer = setTimeout(async () => {
        $results.innerHTML = `<div style="padding:8px 12px;color:#8b949e;font-size:0.875rem">↻ buscando&hellip;</div>`;
        $results.style.display = 'block';
        const res = await InsumosApiClient.searchInsumos(businessId, { q, page: 1, pageSize: 25 });
        if (mySeq !== searchSeq) return; // request stale
        if (!res.ok) {
          $results.innerHTML = `<div style="padding:8px 12px;color:#f85149;font-size:0.875rem">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</div>`;
          return;
        }
        renderSearchResults(res.data?.items || []);
      }, 300);
    });

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData($form);
      const patch = {};
      const newDesc = fd.get('descripcion_raw');
      if (newDesc !== null && newDesc !== (it.descripcion_raw || it.producto || '')) patch.descripcion_raw = newDesc;
      // OCR-T1 (bit #5260): antes Number() pelado sobre inputs type=number — la
      // tablet es-CO escribia "1.234,56", el browser saneaba el value a "" y
      // Number("") = 0 guardaba un CERO fantasma. Ahora: parser colombiano y
      // FAIL-LOUD — texto invalido bloquea el submit con mensaje, jamas 0.
      const campos = [
        { name: 'cantidad', label: 'Cantidad', actual: it.cantidad || 0, key: 'cantidad' },
        { name: 'precio_unit', label: 'Precio unitario', actual: it.precio_unit || 0, key: 'precio_unit' },
        { name: 'subtotal', label: 'Subtotal', actual: it.subtotal || 0, key: 'subtotal' }
      ];
      for (const c of campos) {
        const v = parsearNumeroColombiano(fd.get(c.name));
        if (v !== null && isNaN(v)) {
          $result.innerHTML = `<span style="color:#f85149">${escHtml(c.label)}: "${escHtml(String(fd.get(c.name)))}" no es un numero valido. Usa formato 1.234,56 o 1234.56.</span>`;
          return;
        }
        if (v !== null && v < 0) {
          $result.innerHTML = `<span style="color:#f85149">${escHtml(c.label)} no puede ser negativo.</span>`;
          return;
        }
        if (v !== null && v !== c.actual) patch[c.key] = v;
      }
      const newMatchStatus = fd.get('match_status');
      if (newMatchStatus && newMatchStatus !== it.match_status) patch.match_status = newMatchStatus;
      const newInsumo = (fd.get('insumo_id') || '').trim();
      if (newInsumo === '' && it.insumo_id) patch.insumo_id = null;
      else if (newInsumo !== '' && newInsumo !== (it.insumo_id || '')) patch.insumo_id = newInsumo;

      // FV-T5: campos de empaque (vacio = "no lo se", NO se manda para no pisar
      // con null lo que el OCR o un alias ya habian resuelto).
      const vCantFact = parsearNumeroColombiano(fd.get('cantidad_factura'));
      if (vCantFact !== null && isNaN(vCantFact)) {
        $result.innerHTML = `<span style="color:#f85149">Cantidad en factura: "${escHtml(String(fd.get('cantidad_factura')))}" no es un numero valido.</span>`;
        return;
      }
      if (vCantFact !== null && vCantFact !== (it.cantidad_factura ?? null)) patch.cantidad_factura = vCantFact;
      const vFactor = parsearNumeroColombiano(fd.get('factor_empaque'));
      if (vFactor !== null && isNaN(vFactor)) {
        $result.innerHTML = `<span style="color:#f85149">Factor de conversion: "${escHtml(String(fd.get('factor_empaque')))}" no es un numero valido.</span>`;
        return;
      }
      if (vFactor !== null && vFactor !== (it.factor_empaque ?? null)) patch.factor_empaque = vFactor;
      // recalc=true solo si el usuario movio cantidad/subtotal/empaque: le pide al
      // backend (FV-T2) repetir el subtotal anclado del lado servidor en vez de
      // confiar en el numero que calculo el navegador.
      if (recalcTocado && (patch.cantidad !== undefined || patch.subtotal !== undefined ||
          patch.cantidad_factura !== undefined || patch.factor_empaque !== undefined)) {
        patch.recalc = true;
      }

      const willHaveInsumo = patch.insumo_id !== undefined ? patch.insumo_id : it.insumo_id;
      const willMatch = patch.match_status || it.match_status;
      if (willMatch === 'MATCH' && !willHaveInsumo) {
        $result.innerHTML = `<span style="color:#f85149">match_status=MATCH requiere insumo_id.</span>`;
        return;
      }
      if (Object.keys(patch).length === 0) {
        $result.innerHTML = `<span style="color:#d29922">No hay cambios.</span>`;
        return;
      }

      $result.innerHTML = `<span style="color:#8b949e">↻ Guardando&hellip;</span>`;
      const res = await ComprasApiClient.updateItem(businessId, facturaId, it.id, patch);
      if (!res.ok) {
        $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
        return;
      }
      const effect = res.data.movimiento_effect;
      const effectMsg = effect === 'created' ? '+ movimiento creado'
                     : effect === 'deleted' ? '+ movimiento eliminado'
                     : effect === 'kept'    ? '+ movimiento actualizado'
                     : '';
      $result.innerHTML = `<span style="color:#3fb950">✓ Item guardado ${effectMsg}.</span>`;
      state.selectedFacturaDetail = null;
      setTimeout(() => { close(); renderActiveTab(); }, 800);
    });
  }

  function openEditarMovimientoModal(m) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const formHtml = `
      <form data-cv-edit-mov-form style="display:flex;flex-direction:column;gap:12px">
        <div style="padding:8px 12px;background:#161b22;border:1px solid #30363d;border-radius:6px;color:#8b949e;font-size:0.875rem">
          Tipo: <strong style="color:#c9d1d9">${escHtml(ComprasApiClient.tipoMovMeta(m.tipo).label)}</strong> &middot;
          Insumo: <strong style="color:#c9d1d9">${escHtml(m.insumo_nombre || m.insumo_id || '-')}</strong>
          <div style="margin-top:4px;font-style:italic">Tipo e insumo no editables (atomos identidad). Para cambiar, eliminar y crear nuevo.</div>
        </div>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Cantidad
          <input name="cantidad" type="number" step="0.001" min="0" class="form-input form-input-sm" value="${m.cantidad || 0}">
        </label>
        <div style="display:grid;grid-template-columns:2fr 1fr;gap:10px">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Fecha operativa
            <input name="fecha" type="date" class="form-input form-input-sm" value="${escHtml(m.fecha || '')}">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
            Hora operativa
            <input name="hora" type="time" class="form-input form-input-sm" value="${escHtml((m.hora || '').slice(0,5))}">
          </label>
        </div>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Usuario
          <input name="usuario" type="text" class="form-input form-input-sm" value="${escHtml(m.usuario || '')}">
        </label>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Referencia / motivo
          <input name="referencia" type="text" class="form-input form-input-sm" value="${escHtml(m.referencia || m.motivo || '')}">
        </label>
        <div data-cv-mov-edit-result style="font-size:0.875rem;min-height:18px"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
          <button type="button" data-cv-mov-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="submit" class="btn-primary btn-sm">Guardar cambios</button>
        </div>
      </form>
    `;
    const { overlay, close } = createModalOverlay(`✏ Editar movimiento ${escHtml(m.referencia || m.id)}`, formHtml);
    const $form = overlay.querySelector('[data-cv-edit-mov-form]');
    const $result = overlay.querySelector('[data-cv-mov-edit-result]');
    overlay.querySelector('[data-cv-mov-cancel]').addEventListener('click', close);

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData($form);
      const patch = {};
      const newCant = Number(fd.get('cantidad'));
      if (!isNaN(newCant) && newCant !== (m.cantidad || 0)) patch.cantidad = newCant;
      const newFecha = fd.get('fecha');
      if (newFecha && newFecha !== (m.fecha || '')) patch.fecha = newFecha;
      const newHora = fd.get('hora');
      if (newHora && newHora !== (m.hora || '').slice(0,5)) patch.hora = newHora;
      const newUsuario = (fd.get('usuario') || '').trim();
      if (newUsuario !== (m.usuario || '')) patch.usuario = newUsuario;
      const newRef = (fd.get('referencia') || '').trim();
      if (newRef !== (m.referencia || '')) patch.referencia = newRef;

      if (Object.keys(patch).length === 0) {
        $result.innerHTML = `<span style="color:#d29922">No hay cambios.</span>`;
        return;
      }

      $result.innerHTML = `<span style="color:#8b949e">↻ Guardando&hellip;</span>`;
      const res = await ComprasApiClient.updateMovimiento(businessId, m.id, patch);
      if (!res.ok) {
        $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
        return;
      }
      $result.innerHTML = `<span style="color:#3fb950">✓ Guardado.</span>`;
      setTimeout(() => { close(); loadMovimientos(); }, 500);
    });
  }

  function confirmDeleteMovimiento(m) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const formHtml = `
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="padding:12px 14px;background:#f8514922;border:1px solid #f85149;border-radius:6px;color:#f85149;font-size:0.875rem">
          ⚠ Vas a eliminar permanentemente el movimiento <strong>${escHtml(m.referencia || m.id)}</strong>.
          Insumo afectado: <strong>${escHtml(m.insumo_nombre || m.insumo_id || '-')}</strong>,
          cantidad <strong>${escHtml(m.cantidad)}</strong>.
          <div style="margin-top:6px;font-style:italic">Esta accion no se puede deshacer.</div>
        </div>
        <div data-cv-mov-del-result style="font-size:0.875rem;min-height:18px"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" data-cv-del-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="button" data-cv-del-confirm class="btn-primary btn-sm" style="background:#f85149;border-color:#f85149">🗑 Eliminar definitivamente</button>
        </div>
      </div>
    `;
    const { overlay, close } = createModalOverlay('🗑 Confirmar eliminacion', formHtml);
    const $result = overlay.querySelector('[data-cv-mov-del-result]');
    overlay.querySelector('[data-cv-del-cancel]').addEventListener('click', close);
    overlay.querySelector('[data-cv-del-confirm]').addEventListener('click', async () => {
      $result.innerHTML = `<span style="color:#8b949e">↻ Eliminando&hellip;</span>`;
      const res = await ComprasApiClient.deleteMovimiento(businessId, m.id);
      if (!res.ok) {
        $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
        return;
      }
      $result.innerHTML = `<span style="color:#3fb950">✓ Eliminado.</span>`;
      setTimeout(() => { close(); loadMovimientos(); }, 500);
    });
  }

  /**
   * Confirma eliminacion de factura. Flujo 2 pasos:
   *  Paso 1: deleteFactura(force=false) -> si 200/204 OK, redirect Tab Facturas.
   *  Paso 2: si 409 (movimientos asociados) -> mostrar warning + boton "Eliminar de todas formas"
   *          que reintenta con force=true.
   * F1-P / PANEL-COMPRAS-EDICION-AVANZADA-V1-001 (bit #1038).
   */
  function confirmDeleteFacturaModal(f) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const matchMeta = ComprasApiClient.matchStatusMeta(f.match_status);
    const itemsCount = f.items_count || (f.items || []).length || 0;
    const matchWarning = f.match_status === 'MATCH'
      ? `<div style="margin-top:6px;padding:6px 10px;background:#d2992222;border:1px dashed #d29922;border-radius:4px;color:#d29922;font-size:0.875rem">
           ⚠ Esta factura tiene match completo al inventario. Al eliminar se removeran los movimientos_inventario asociados (entradas que el inventario ya conto).
         </div>`
      : '';
    const formHtml = `
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="padding:12px 14px;background:#f8514922;border:1px solid #f85149;border-radius:6px;color:#f85149;font-size:0.875rem">
          ⚠ Vas a eliminar permanentemente la factura <strong>${formatNullable(f.numero)}</strong>
          (${formatNullable(f.proveedor)} &middot; ${formatNullable(f.fecha)})
          con <strong>${itemsCount}</strong> item${itemsCount === 1 ? '' : 's'}
          y total <strong>$${formatCurrency(f.total)}</strong>.
          <div style="margin-top:6px;font-style:italic">Esta accion borra la factura, sus items y todos los movimientos asociados (CASCADE). No se puede deshacer.</div>
          ${matchWarning}
        </div>
        <div data-cv-fac-del-result style="font-size:0.875rem;min-height:18px"></div>
        <div data-cv-fac-del-actions style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" data-cv-fac-del-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="button" data-cv-fac-del-confirm class="btn-primary btn-sm" style="background:#f85149;border-color:#f85149">🗑 Eliminar definitivamente</button>
        </div>
      </div>
    `;
    const { overlay, close } = createModalOverlay('🗑 Confirmar eliminacion de factura', formHtml);
    const $result = overlay.querySelector('[data-cv-fac-del-result]');
    const $actions = overlay.querySelector('[data-cv-fac-del-actions]');
    overlay.querySelector('[data-cv-fac-del-cancel]').addEventListener('click', close);

    const tryDelete = async (force) => {
      $result.innerHTML = `<span style="color:#8b949e">↻ ${force ? 'Eliminando con override' : 'Eliminando'}&hellip;</span>`;
      const res = await ComprasApiClient.deleteFactura(businessId, f.id, { force });
      if (res.ok) {
        $result.innerHTML = `<span style="color:#3fb950">✓ Factura eliminada${force ? ' (incluidos movimientos)' : ''}.</span>`;
        setTimeout(() => {
          close();
          state.selectedFacturaId = null;
          state.selectedFacturaDetail = null;
          state.activeTab = 'facturas';
          renderActiveTab();
        }, 600);
        return;
      }
      // 409 -> ofrecer override force=true
      const errCode = res.error?.code;
      const isConflict = errCode === 409 || errCode === 'CONFLICT' || String(errCode).includes('409');
      if (isConflict && !force) {
        const detail = res.error?.message || res.error?.detail || 'La factura tiene movimientos asociados que requieren confirmacion explicita.';
        $result.innerHTML = `
          <div style="padding:8px 12px;background:#d2992222;border:1px dashed #d29922;border-radius:4px;color:#d29922;font-size:0.875rem">
            ⚠ ${escHtml(detail)}
          </div>`;
        $actions.innerHTML = `
          <button type="button" data-cv-fac-del-cancel2 class="btn-ghost btn-sm">Cancelar</button>
          <button type="button" data-cv-fac-del-force class="btn-primary btn-sm"
                  style="background:#f85149;border-color:#f85149">🗑 Eliminar incluyendo movimientos</button>
        `;
        $actions.querySelector('[data-cv-fac-del-cancel2]').addEventListener('click', close);
        $actions.querySelector('[data-cv-fac-del-force]').addEventListener('click', () => tryDelete(true));
        return;
      }
      // Otro error
      $result.innerHTML = `<span style="color:#f85149">Error: ${escHtml(res.error?.message || JSON.stringify(res.error || {}))}</span>`;
    };

    overlay.querySelector('[data-cv-fac-del-confirm]').addEventListener('click', () => tryDelete(false));
  }

  /**
   * F5-P: confirma todos los items NO-MATCH con candidato top via PATCH item masivo.
   * El backend hook TryConfirmEgresoIfAllItemsMatch flipa egresos.confirmed=true si
   * tras estos PATCH todos los items de la factura quedan en MATCH.
   *
   * Items con candidatos[] cuya max similitud >= 0.30 -> auto-match al top.
   * Items sin candidatos -> skip + warning (requieren match manual via Editar).
   * PANEL-COMPRAS-EDICION-AVANZADA-V1-001 / F5-P (bit #1047).
   */
  function autoConfirmAllItems(f) {
    const businessId = getSelectedBusiness();
    if (!businessId) return;
    const items = (f.items || []).filter(it => !itemResuelto(it));
    const autoConfirmable = items.filter(it => {
      const cands = it.candidatos || [];
      return cands.length > 0 && Math.max(...cands.map(c => c.similitud || 0)) >= 0.30;
    });
    const sinCandidatos = items.length - autoConfirmable.length;

    const formHtml = `
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="padding:10px 14px;background:#1f6feb22;border:1px solid #1f6feb;border-radius:6px;color:#79c0ff;font-size:0.875rem">
          Vas a aplicar MATCH automatico a <strong>${autoConfirmable.length}</strong> item${autoConfirmable.length === 1 ? '' : 's'}
          usando el candidato OCR con mayor similitud (≥30%).
          ${sinCandidatos > 0
            ? `<div style="margin-top:6px;color:#d29922">⚠ ${sinCandidatos} item${sinCandidatos === 1 ? '' : 's'} sin candidatos: requieren match manual via Editar (busca en catalogo).</div>`
            : ''
          }
          <div style="margin-top:6px;font-size:0.875rem;color:#c9d1d9;font-style:italic">Cuando todos los items queden en MATCH, el backend confirma la factura automaticamente para que Desktop la descargue.</div>
        </div>
        <div data-cv-auto-result style="font-size:0.875rem;min-height:18px"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" data-cv-auto-cancel class="btn-ghost btn-sm">Cancelar</button>
          <button type="button" data-cv-auto-confirm-go class="btn-primary btn-sm" ${autoConfirmable.length === 0 ? 'disabled style="opacity:0.5;cursor:not-allowed"' : ''}>
            ✓ Aplicar auto-match a ${autoConfirmable.length}
          </button>
        </div>
      </div>
    `;
    const { overlay, close } = createModalOverlay('✓ Confirmar items con MATCH automatico', formHtml);
    const $result = overlay.querySelector('[data-cv-auto-result]');
    overlay.querySelector('[data-cv-auto-cancel]').addEventListener('click', close);

    overlay.querySelector('[data-cv-auto-confirm-go]')?.addEventListener('click', async () => {
      if (autoConfirmable.length === 0) return;
      const $go = overlay.querySelector('[data-cv-auto-confirm-go]');
      $go.disabled = true;
      $go.style.opacity = '0.5';
      let okCount = 0;
      const errores = [];
      for (let i = 0; i < autoConfirmable.length; i++) {
        const it = autoConfirmable[i];
        const top = (it.candidatos || []).slice().sort((a, b) => (b.similitud || 0) - (a.similitud || 0))[0];
        $result.innerHTML = `<span style="color:#8b949e">↻ Procesando ${i + 1}/${autoConfirmable.length}: ${escHtml(it.producto)}&hellip;</span>`;
        const res = await ComprasApiClient.updateItem(businessId, f.id, it.id, {
          match_status: 'MATCH',
          insumo_id: top.producto_existente_id,
          match_score: top.similitud || 1
        });
        if (res.ok) {
          okCount++;
        } else {
          errores.push({ producto: it.producto, error: res.error?.message || JSON.stringify(res.error || {}) });
        }
      }
      const erroresHtml = errores.length > 0
        ? `<div style="margin-top:6px;color:#f85149;font-size:0.875rem">
             ${errores.map(e => `&bull; ${escHtml(e.producto)}: ${escHtml(e.error)}`).join('<br>')}
           </div>`
        : '';
      $result.innerHTML = `
        <span style="color:#3fb950">✓ ${okCount}/${autoConfirmable.length} items confirmados${sinCandidatos > 0 ? ` (${sinCandidatos} requieren match manual)` : ''}.</span>
        ${erroresHtml}
      `;
      // Reload detalle al final (backend ya flipo egreso.confirmed si todos quedaron MATCH)
      setTimeout(() => {
        close();
        state.selectedFacturaDetail = null;
        renderActiveTab();
      }, errores.length > 0 ? 2500 : 1200);
    });
  }
}
